/**
 * vendorPortalI18n.js — Phase 7g.15.36 (Rick directive 2026-05-19)
 *
 * Centralised i18n strings for the simplified Vendor Portal home
 * (DashboardHome) + the Vendor Profile / Edit Listing surface
 * (VendorDashboardTiered). Keeps LanguageContext.jsx from ballooning
 * further and gives translators a single file to edit.
 *
 * 11 languages: EN, ES, FR, PT, DE, ZH, JA, KO, IT, RU, HT.
 * (IT/RU/HT machine-translated 2026-06-12 with token/brand validation.)
 *
 * Conventions:
 *   vh_* = Vendor Home (DashboardHome)
 *   vp_* = Vendor Profile (VendorDashboardTiered)
 *
 * Pure data. No runtime behavior here.
 */

const VENDOR_PORTAL_I18N = {
  en: {
    // ── DashboardHome banners ────────────────────────────────
    vh_app_pending_title: "Application Pending Review",
    vh_app_pending_desc: "Your vendor application is being reviewed. We'll notify you once approved (usually 24–48 hours).",
    vh_stripe_pending_title: "Stripe verification in progress",
    vh_stripe_pending_desc: "Your details are submitted. Stripe is verifying your account — usually a few minutes, sometimes up to 24h. You can accept and complete bookings now; earnings will queue until Stripe finishes verifying.",
    vh_stripe_pending_refresh: "Refresh",
    vh_stripe_warning_title: "Finish Stripe Payout Setup",
    vh_stripe_warning_desc: "Payouts won't be released until Stripe onboarding is complete.",
    vh_stripe_warning_cta: "Finish",
    vh_welcome_back: "Welcome back,",
    vh_signed_in_as: "Signed in as {name}",
    // ── Next Best Action ─────────────────────────────────────
    vh_next_best_action_label: "Next best action",
    vh_action_profile_title: "Complete your profile",
    vh_action_profile_desc: "Add your business name and bio so customers can find and trust you.",
    vh_action_profile_cta: "Complete profile",
    vh_action_photos_title: "Add your first photos",
    vh_action_photos_desc: "Profiles with photos get up to 5× more bookings. Upload at least one to get started.",
    vh_action_photos_cta: "Upload photos",
    vh_action_equipment_title: "Boost your equipment readiness",
    vh_action_equipment_desc: "You're at {pct}%. Hit 80%+ to qualify for priority dispatch.",
    vh_action_equipment_cta: "Open checklist",
    vh_action_bookings_title: "{count} booking offer waiting",
    vh_action_bookings_desc: "Respond quickly — offers reassign automatically after a few minutes.",
    vh_action_bookings_cta: "Review offers",
    vh_action_upgrade_title: "Upgrade to keep more of your earnings",
    vh_action_upgrade_desc: "Pro Premier vendors keep 85% of every wash and get priority placement on the map.",
    vh_action_upgrade_cta: "See plans",
    vh_action_allset_title: "You're all set",
    vh_action_allset_desc: "Profile, photos, and equipment look great. Keep delivering 5-star service.",
    vh_action_allset_cta: "View jobs",
    // ── Grouped cards ────────────────────────────────────────
    vh_group_setup_title: "Setup & tools",
    vh_group_setup_subtitle: "Get ready to deliver",
    vh_group_grow_title: "Grow your business",
    vh_group_grow_subtitle: "More jobs, better routing, bigger payouts",
    vh_link_equipment_title: "Equipment checklist",
    vh_link_equipment_desc: "Track your gear readiness",
    vh_link_training_title: "Training & tutorials",
    vh_link_training_desc: "Learn how to deliver 5-star service",
    vh_link_listing_title: "Your listing preview",
    vh_link_listing_desc: "See how customers view your profile",
    vh_link_referrals_title: "Referral program",
    vh_link_referrals_desc: "Earn $50 for every vendor you refer",
    vh_link_schedule_title: "Today's schedule",
    vh_link_schedule_desc: "Timeline view of today's bookings",
    vh_link_route_title: "Smart route",
    vh_link_route_desc: "Optimize your driving order, save time & fuel",
    // ── Vendor Profile (VendorDashboardTiered) ───────────────
    vp_header_subtitle_elite: "Maximum visibility & lowest fees",
    vp_header_subtitle_pro: "Enhanced visibility & features",
    vp_header_subtitle_basic: "Basic listing — upgrade for more features",
    vp_header_subtitle_luxury: "Top-of-marketplace placement · lowest commission",
    vp_upgrade_to_pro_premier: "Upgrade to Pro Premier",
    vp_upgrade_to_elite_vip: "Upgrade to Elite VIP",
    vp_upgrade_to_luxury: "Upgrade to Luxury Concierge",
    vp_platform_fee_label: "{pct}% Platform Fee",
    vp_basic_fee_warning_title: "You're paying 20% per wash",
    vp_basic_fee_warning_desc: "Upgrade to Pro Premier (15%) or Elite VIP (10%) to keep more of your earnings.",
    vp_recent_jobs_title: "Recent jobs",
    vp_recent_jobs_more_hidden: "+{count} more jobs hidden",
    vp_recent_jobs_upgrade_link: "Upgrade to see all →",
    vp_listing_preview_section_title: "Customer preview",
    vp_visibility_max: "Maximum visibility",
    vp_visibility_enhanced: "Enhanced visibility",
    vp_visibility_limited: "Limited visibility",
    vp_bio_label: "Business bio",
    vp_bio_chars_count: "{count}/{max} characters",
    vp_bio_upgrade_hint: "Upgrade for {max} chars",
    vp_bio_basic_lock: "Upgrade to Pro Premier for a longer, more detailed bio that attracts more customers.",
    vp_work_photos_label: "Work photos",
    vp_photos_count_label: "{count}/{max} photos",
    vp_add_photo: "Add photo",
    vp_uploading: "Uploading…",
    vp_remove_photo: "Remove photo",
    vp_photos_locked_aria: "Upgrade to unlock more photo slots",
    vp_photos_copy_basic: "Basic / Free — up to 2 photos · upgrade to Pro Premier for 5, Elite VIP for 10, or Luxury Concierge for 15.",
    vp_photos_copy_pro: "Pro Premier — up to 5 photos · upgrade to Elite VIP for 10 (or Luxury Concierge for 15).",
    vp_photos_copy_elite: "Elite VIP — 10-photo gallery · expanded visibility & priority listing placement · upgrade to Luxury for 15.",
    vp_photos_copy_luxury: "Luxury Concierge — top premium listing · 15-photo gallery · highest marketplace positioning.",
    vp_photos_unlock_more: "Unlock more photos →",
    vp_video_portfolio: "Video portfolio",
    vp_video_elite_exclusive: "Elite exclusive",
    vp_video_add: "Add a video showcasing your work",
    vp_video_up_to_2min: "Up to 2 minutes",
    vp_video_locked_title: "Video portfolio",
    vp_video_locked_desc: "Upgrade to Elite VIP to add video content.",
    vp_video_locked_cta: "Unlock",
    // ── Availability Toggle ─────────────────────────────────
    va_available: "Available",
    va_not_available: "Not available",
    va_visible_desc: "You're visible to customers and can receive bookings",
    va_hidden_desc: "You're hidden from searches — no new bookings",
    va_go_offline: "Go offline",
    va_go_online: "Go online",
    va_check_visible_searches: "Visible in searches",
    va_check_receiving_bookings: "Receiving bookings",
    va_check_on_the_map: "On the map",
    va_check_customer_notifications: "Customer notifications",
    va_last_updated: "Last updated: {time}",
    va_toggle_error: "Failed to update availability status. Please try again.",
    // ── Vendor Subscription page ────────────────────────────
    vs_page_title: "Vendor subscription plans",
    vs_page_subtitle: "Upgrade your plan to earn more per job, get priority access to bookings, and stand out to customers.",
    vs_current_plan: "Current plan",
    vs_commission_rate: "Commission rate",
    vs_benefit_earn_title: "Earn more per job",
    vs_benefit_earn_desc: "Lower commission rates mean you keep more of what you earn. Elite VIP members save up to 10% per job compared to Standard.",
    vs_benefit_priority_title: "Priority job access",
    vs_benefit_priority_desc: "Get first dibs on the best jobs. Premium members receive offers before standard vendors.",
    vs_benefit_standout_title: "Stand out to customers",
    vs_benefit_standout_desc: "Premium badges and featured placement help you attract more customers and build trust.",
    vs_most_popular_badge: "Most popular",
    vs_per_month: "/month",
    vs_platform_fee_per_wash: "Platform fee · {pct}% per wash",
    vs_select_plan: "Select plan",
    vs_current_plan_badge: "Current plan",
    // Phase 7g.15.61 (Sweep #5) — long-form VendorSubscription copy
    vs_platform_commission: "Platform Commission",
    vs_monthly_label: "Monthly",
    vs_current_plan_btn: "Current Plan",
    vs_upgrade_btn: "Upgrade",
    vs_switch_plan_btn: "Switch Plan",
    vs_savings_card_title: "See Your Savings",
    vs_savings_card_desc: "Calculate how much more you could earn with a premium plan",
    vs_savings_if_earn: "If you earn {amount}/month",
    vs_savings_standard_keep: "Standard: Keep {amount}",
    vs_savings_pro_keep: "Pro Premier: Keep {amount}",
    vs_savings_elite_keep: "Elite VIP: Keep {amount}",
    vs_faq_title: "Frequently Asked Questions",
    vs_faq_cancel_q: "Can I cancel anytime?",
    vs_faq_cancel_a: "Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.",
    vs_faq_commission_q: "When do commission rates change?",
    vs_faq_commission_a: "Your new commission rate takes effect immediately after subscribing. All future jobs will use your new rate.",
    vs_faq_payment_q: "What payment methods are accepted?",
    vs_faq_payment_a: "We accept all major credit cards through our secure payment processor, Stripe.",
    vs_toast_cancelled: "Subscription cancelled. You are now on the Standard plan.",
    vs_toast_failed: "Failed to process subscription",
    vs_toast_verify_timeout: "Subscription verification timed out. Please refresh.",
    vs_toast_welcome_to: "Welcome to {name}!",
    // Tier features — Basic / Pro Premier / Elite VIP / Luxury
    vs_feat_basic_listing: "Basic listing",
    vs_feat_standard_commission: "Standard commission (20%)",
    vs_feat_access_offers: "Access to all job offers",
    vs_feat_basic_support: "Basic support",
    vs_feat_priority_offers: "Priority job offers",
    vs_feat_featured_placement: "Featured placement",
    vs_feat_priority_support: "Priority support",
    vs_feat_lower_commission: "Lower commission rates",
    vs_feat_priority_dispatch: "Priority in dispatch queue",
    vs_feat_reduced_commission: "Reduced commission (15%)",
    vs_feat_featured_search: "Featured in search results",
    vs_feat_priority_support_response: "Priority support response",
    vs_feat_detailed_analytics: "Detailed analytics dashboard",
    vs_feat_professional_badge: "Professional badge on profile",
    vs_feat_top_placement: "Top placement in all searches",
    vs_feat_lowest_commission: "Lowest commission (10%)",
    vs_feat_first_access_premium: "First access to premium jobs",
    vs_feat_vip_support: "VIP dedicated support line",
    vs_feat_advanced_analytics: "Advanced analytics & insights",
    vs_feat_elite_badge: "Elite badge on profile",
    vs_feat_marketing_features: "Marketing feature opportunities",
    vs_feat_exclusive_events: "Exclusive vendor events access",
    vs_feat_top_placement_guarantee: "Top placement guarantee",
    vs_feat_vip_dedicated_support: "VIP dedicated support",
  },

  es: {
    vh_app_pending_title: "Solicitud en revisión",
    vh_app_pending_desc: "Tu solicitud de proveedor está siendo revisada. Te avisaremos cuando esté aprobada (normalmente 24–48 horas).",
    vh_stripe_pending_title: "Verificación de Stripe en curso",
    vh_stripe_pending_desc: "Tus datos fueron enviados. Stripe está verificando tu cuenta — normalmente unos minutos, hasta 24h. Puedes aceptar y completar reservas; tus ganancias se acumularán hasta que Stripe termine.",
    vh_stripe_pending_refresh: "Actualizar",
    vh_stripe_warning_title: "Termina la configuración de pagos en Stripe",
    vh_stripe_warning_desc: "Los pagos no se liberarán hasta completar la incorporación en Stripe.",
    vh_stripe_warning_cta: "Terminar",
    vh_welcome_back: "Bienvenido,",
    vh_signed_in_as: "Sesión iniciada como {name}",
    vh_next_best_action_label: "Siguiente acción recomendada",
    vh_action_profile_title: "Completa tu perfil",
    vh_action_profile_desc: "Agrega el nombre de tu negocio y una bio para que los clientes te encuentren y confíen en ti.",
    vh_action_profile_cta: "Completar perfil",
    vh_action_photos_title: "Sube tus primeras fotos",
    vh_action_photos_desc: "Los perfiles con fotos reciben hasta 5× más reservas. Sube al menos una para empezar.",
    vh_action_photos_cta: "Subir fotos",
    vh_action_equipment_title: "Mejora tu lista de equipamiento",
    vh_action_equipment_desc: "Estás al {pct}%. Llega al 80%+ para acceder al despacho prioritario.",
    vh_action_equipment_cta: "Abrir lista",
    vh_action_bookings_title: "{count} oferta de reserva esperando",
    vh_action_bookings_desc: "Responde rápido — las ofertas se reasignan automáticamente tras unos minutos.",
    vh_action_bookings_cta: "Revisar ofertas",
    vh_action_upgrade_title: "Mejora tu plan para conservar más ingresos",
    vh_action_upgrade_desc: "Los proveedores Pro Premier conservan el 85% de cada lavado y obtienen mejor posición en el mapa.",
    vh_action_upgrade_cta: "Ver planes",
    vh_action_allset_title: "¡Todo listo!",
    vh_action_allset_desc: "Perfil, fotos y equipamiento se ven genial. Sigue brindando servicio de 5 estrellas.",
    vh_action_allset_cta: "Ver trabajos",
    vh_group_setup_title: "Configuración y herramientas",
    vh_group_setup_subtitle: "Prepárate para trabajar",
    vh_group_grow_title: "Haz crecer tu negocio",
    vh_group_grow_subtitle: "Más trabajos, mejor ruta, mayores pagos",
    vh_link_equipment_title: "Lista de equipamiento",
    vh_link_equipment_desc: "Controla tu nivel de preparación",
    vh_link_training_title: "Tutoriales y formación",
    vh_link_training_desc: "Aprende a dar servicio de 5 estrellas",
    vh_link_listing_title: "Vista previa de tu listado",
    vh_link_listing_desc: "Cómo te ven los clientes",
    vh_link_referrals_title: "Programa de referidos",
    vh_link_referrals_desc: "Gana $50 por cada proveedor que refieras",
    vh_link_schedule_title: "Agenda de hoy",
    vh_link_schedule_desc: "Vista cronológica de las reservas de hoy",
    vh_link_route_title: "Ruta inteligente",
    vh_link_route_desc: "Optimiza el orden, ahorra tiempo y combustible",
    vp_header_subtitle_elite: "Máxima visibilidad y comisiones más bajas",
    vp_header_subtitle_pro: "Visibilidad mejorada y más funciones",
    vp_header_subtitle_basic: "Listado básico — mejora para más funciones",
    vp_header_subtitle_luxury: "Mejor posicionamiento · comisión más baja",
    vp_upgrade_to_pro_premier: "Subir a Pro Premier",
    vp_upgrade_to_elite_vip: "Subir a Elite VIP",
    vp_upgrade_to_luxury: "Subir a Luxury Concierge",
    vp_platform_fee_label: "{pct}% de comisión",
    vp_basic_fee_warning_title: "Estás pagando 20% por lavado",
    vp_basic_fee_warning_desc: "Mejora a Pro Premier (15%) o Elite VIP (10%) para conservar más ingresos.",
    vp_recent_jobs_title: "Trabajos recientes",
    vp_recent_jobs_more_hidden: "+{count} trabajos más ocultos",
    vp_recent_jobs_upgrade_link: "Mejora para ver todos →",
    vp_listing_preview_section_title: "Vista del cliente",
    vp_visibility_max: "Máxima visibilidad",
    vp_visibility_enhanced: "Visibilidad mejorada",
    vp_visibility_limited: "Visibilidad limitada",
    vp_bio_label: "Bio del negocio",
    vp_bio_chars_count: "{count}/{max} caracteres",
    vp_bio_upgrade_hint: "Mejora para {max} caracteres",
    vp_bio_basic_lock: "Sube a Pro Premier para una bio más larga y detallada que atraiga más clientes.",
    vp_work_photos_label: "Fotos de trabajo",
    vp_photos_count_label: "{count}/{max} fotos",
    vp_add_photo: "Agregar foto",
    vp_uploading: "Subiendo…",
    vp_remove_photo: "Quitar foto",
    vp_photos_locked_aria: "Mejora para desbloquear más espacios de fotos",
    vp_photos_copy_basic: "Básico / Gratis — hasta 2 fotos · sube a Pro Premier para 5, Elite VIP para 10 o Luxury Concierge para 15.",
    vp_photos_copy_pro: "Pro Premier — hasta 5 fotos · sube a Elite VIP para 10 (o Luxury Concierge para 15).",
    vp_photos_copy_elite: "Elite VIP — galería de 10 fotos · mayor visibilidad y mejor posicionamiento · sube a Luxury para 15.",
    vp_photos_copy_luxury: "Luxury Concierge — listado premium · galería de 15 fotos · máximo posicionamiento.",
    vp_photos_unlock_more: "Desbloquear más fotos →",
    vp_video_portfolio: "Vídeo de cartera",
    vp_video_elite_exclusive: "Exclusivo Elite",
    vp_video_add: "Añade un vídeo mostrando tu trabajo",
    vp_video_up_to_2min: "Hasta 2 minutos",
    vp_video_locked_title: "Vídeo de cartera",
    vp_video_locked_desc: "Sube a Elite VIP para añadir vídeo.",
    vp_video_locked_cta: "Desbloquear",
    va_available: "Disponible",
    va_not_available: "No disponible",
    va_visible_desc: "Eres visible para los clientes y puedes recibir reservas",
    va_hidden_desc: "Estás oculto en las búsquedas — sin nuevas reservas",
    va_go_offline: "Desconectarme",
    va_go_online: "Conectarme",
    va_check_visible_searches: "Visible en búsquedas",
    va_check_receiving_bookings: "Recibiendo reservas",
    va_check_on_the_map: "En el mapa",
    va_check_customer_notifications: "Notificaciones al cliente",
    va_last_updated: "Última actualización: {time}",
    va_toggle_error: "No se pudo actualizar el estado. Inténtalo de nuevo.",
    vs_page_title: "Planes de suscripción para proveedores",
    vs_page_subtitle: "Mejora tu plan para ganar más por trabajo, tener acceso prioritario a las reservas y destacar entre los clientes.",
    vs_current_plan: "Plan actual",
    vs_commission_rate: "Comisión",
    vs_benefit_earn_title: "Gana más por trabajo",
    vs_benefit_earn_desc: "Comisiones más bajas significan que conservas más de lo que ganas. Los miembros Elite VIP ahorran hasta 10% por trabajo vs. Standard.",
    vs_benefit_priority_title: "Acceso prioritario a trabajos",
    vs_benefit_priority_desc: "Sé el primero en los mejores trabajos. Los miembros premium reciben ofertas antes que los proveedores estándar.",
    vs_benefit_standout_title: "Destácate ante los clientes",
    vs_benefit_standout_desc: "Insignias premium y colocación destacada te ayudan a atraer más clientes y a generar confianza.",
    vs_most_popular_badge: "Más popular",
    vs_per_month: "/mes",
    vs_platform_fee_per_wash: "Comisión · {pct}% por lavado",
    vs_select_plan: "Elegir plan",
    vs_current_plan_badge: "Plan actual",
    vs_platform_commission: "Comisión de Plataforma",
    vs_monthly_label: "Mensual",
    vs_current_plan_btn: "Plan Actual",
    vs_upgrade_btn: "Mejorar",
    vs_switch_plan_btn: "Cambiar de Plan",
    vs_savings_card_title: "Mira tus Ahorros",
    vs_savings_card_desc: "Calcula cuánto más podrías ganar con un plan premium",
    vs_savings_if_earn: "Si ganas {amount}/mes",
    vs_savings_standard_keep: "Estándar: Conservas {amount}",
    vs_savings_pro_keep: "Pro Premier: Conservas {amount}",
    vs_savings_elite_keep: "Elite VIP: Conservas {amount}",
    vs_faq_title: "Preguntas Frecuentes",
    vs_faq_cancel_q: "¿Puedo cancelar en cualquier momento?",
    vs_faq_cancel_a: "¡Sí! Puedes cancelar tu suscripción en cualquier momento. Mantendrás el acceso hasta el final del periodo de facturación.",
    vs_faq_commission_q: "¿Cuándo cambian las tasas de comisión?",
    vs_faq_commission_a: "Tu nueva tasa de comisión se aplica de inmediato al suscribirte. Todos los trabajos futuros usarán la nueva tasa.",
    vs_faq_payment_q: "¿Qué métodos de pago se aceptan?",
    vs_faq_payment_a: "Aceptamos todas las tarjetas de crédito principales a través de nuestro procesador de pagos seguro, Stripe.",
    vs_toast_cancelled: "Suscripción cancelada. Ahora estás en el plan Estándar.",
    vs_toast_failed: "Error al procesar la suscripción",
    vs_toast_verify_timeout: "Se agotó el tiempo de verificación de la suscripción. Por favor actualiza.",
    vs_toast_welcome_to: "¡Bienvenido a {name}!",
    vs_feat_basic_listing: "Listado básico",
    vs_feat_standard_commission: "Comisión estándar (20%)",
    vs_feat_access_offers: "Acceso a todas las ofertas",
    vs_feat_basic_support: "Soporte básico",
    vs_feat_priority_offers: "Ofertas de trabajo prioritarias",
    vs_feat_featured_placement: "Colocación destacada",
    vs_feat_priority_support: "Soporte prioritario",
    vs_feat_lower_commission: "Tarifas de comisión más bajas",
    vs_feat_priority_dispatch: "Prioridad en la cola de despacho",
    vs_feat_reduced_commission: "Comisión reducida (15%)",
    vs_feat_featured_search: "Destacado en los resultados de búsqueda",
    vs_feat_priority_support_response: "Respuesta de soporte prioritaria",
    vs_feat_detailed_analytics: "Panel de analíticas detalladas",
    vs_feat_professional_badge: "Insignia profesional en el perfil",
    vs_feat_top_placement: "Posición destacada en todas las búsquedas",
    vs_feat_lowest_commission: "Comisión más baja (10%)",
    vs_feat_first_access_premium: "Primer acceso a trabajos premium",
    vs_feat_vip_support: "Línea de soporte VIP dedicada",
    vs_feat_advanced_analytics: "Analíticas e información avanzadas",
    vs_feat_elite_badge: "Insignia Elite en el perfil",
    vs_feat_marketing_features: "Oportunidades de marketing destacado",
    vs_feat_exclusive_events: "Acceso a eventos exclusivos para vendedores",
    vs_feat_top_placement_guarantee: "Garantía de posición destacada",
    vs_feat_vip_dedicated_support: "Soporte VIP dedicado",
  },

  fr: {
    vh_app_pending_title: "Candidature en examen",
    vh_app_pending_desc: "Votre candidature de prestataire est en cours d'examen. Nous vous prévenons dès qu'elle est approuvée (généralement 24–48 h).",
    vh_stripe_pending_title: "Vérification Stripe en cours",
    vh_stripe_pending_desc: "Vos informations sont soumises. Stripe vérifie votre compte — généralement quelques minutes, jusqu'à 24 h. Vous pouvez accepter et terminer des réservations ; vos gains seront mis en file d'attente.",
    vh_stripe_pending_refresh: "Actualiser",
    vh_stripe_warning_title: "Terminer la configuration Stripe",
    vh_stripe_warning_desc: "Les paiements ne seront pas versés tant que la configuration Stripe n'est pas terminée.",
    vh_stripe_warning_cta: "Terminer",
    vh_welcome_back: "Bon retour,",
    vh_signed_in_as: "Connecté en tant que {name}",
    vh_next_best_action_label: "Prochaine action recommandée",
    vh_action_profile_title: "Complétez votre profil",
    vh_action_profile_desc: "Ajoutez le nom de votre entreprise et une bio pour que les clients vous trouvent et vous fassent confiance.",
    vh_action_profile_cta: "Compléter le profil",
    vh_action_photos_title: "Ajoutez vos premières photos",
    vh_action_photos_desc: "Les profils avec photos reçoivent jusqu'à 5× plus de réservations. Téléversez-en au moins une.",
    vh_action_photos_cta: "Téléverser des photos",
    vh_action_equipment_title: "Améliorez votre préparation",
    vh_action_equipment_desc: "Vous êtes à {pct} %. Atteignez 80 %+ pour la priorité d'attribution.",
    vh_action_equipment_cta: "Ouvrir la liste",
    vh_action_bookings_title: "{count} offre en attente",
    vh_action_bookings_desc: "Répondez vite — les offres sont réaffectées automatiquement après quelques minutes.",
    vh_action_bookings_cta: "Voir les offres",
    vh_action_upgrade_title: "Passez à un plan supérieur pour garder plus de vos gains",
    vh_action_upgrade_desc: "Les prestataires Pro Premier gardent 85 % de chaque lavage et obtiennent un meilleur placement sur la carte.",
    vh_action_upgrade_cta: "Voir les forfaits",
    vh_action_allset_title: "Tout est prêt",
    vh_action_allset_desc: "Profil, photos et équipement sont au top. Continuez le service 5 étoiles.",
    vh_action_allset_cta: "Voir les missions",
    vh_group_setup_title: "Configuration & outils",
    vh_group_setup_subtitle: "Préparez-vous à livrer",
    vh_group_grow_title: "Développez votre activité",
    vh_group_grow_subtitle: "Plus de missions, meilleurs itinéraires, plus de gains",
    vh_link_equipment_title: "Liste d'équipement",
    vh_link_equipment_desc: "Suivez votre niveau de préparation",
    vh_link_training_title: "Formation & tutoriels",
    vh_link_training_desc: "Apprenez à livrer un service 5 étoiles",
    vh_link_listing_title: "Aperçu de votre fiche",
    vh_link_listing_desc: "Voyez comment les clients vous voient",
    vh_link_referrals_title: "Programme de parrainage",
    vh_link_referrals_desc: "Gagnez 50 $ pour chaque prestataire parrainé",
    vh_link_schedule_title: "Planning du jour",
    vh_link_schedule_desc: "Chronologie des réservations du jour",
    vh_link_route_title: "Itinéraire intelligent",
    vh_link_route_desc: "Optimisez l'ordre, économisez temps et carburant",
    vp_header_subtitle_elite: "Visibilité maximale et frais les plus bas",
    vp_header_subtitle_pro: "Visibilité et fonctionnalités améliorées",
    vp_header_subtitle_basic: "Fiche basique — passez à un plan supérieur",
    vp_header_subtitle_luxury: "Top du marché · commission la plus basse",
    vp_upgrade_to_pro_premier: "Passer à Pro Premier",
    vp_upgrade_to_elite_vip: "Passer à Elite VIP",
    vp_upgrade_to_luxury: "Passer à Luxury Concierge",
    vp_platform_fee_label: "{pct} % de commission",
    vp_basic_fee_warning_title: "Vous payez 20 % par lavage",
    vp_basic_fee_warning_desc: "Passez à Pro Premier (15 %) ou Elite VIP (10 %) pour garder plus de vos gains.",
    vp_recent_jobs_title: "Missions récentes",
    vp_recent_jobs_more_hidden: "+{count} missions masquées",
    vp_recent_jobs_upgrade_link: "Passer à un plan supérieur pour tout voir →",
    vp_listing_preview_section_title: "Aperçu client",
    vp_visibility_max: "Visibilité maximale",
    vp_visibility_enhanced: "Visibilité améliorée",
    vp_visibility_limited: "Visibilité limitée",
    vp_bio_label: "Bio de l'entreprise",
    vp_bio_chars_count: "{count}/{max} caractères",
    vp_bio_upgrade_hint: "Passez à {max} caractères",
    vp_bio_basic_lock: "Passez à Pro Premier pour une bio plus longue et plus détaillée qui attire plus de clients.",
    vp_work_photos_label: "Photos de travail",
    vp_photos_count_label: "{count}/{max} photos",
    vp_add_photo: "Ajouter une photo",
    vp_uploading: "Téléversement…",
    vp_remove_photo: "Supprimer la photo",
    vp_photos_locked_aria: "Passez à un plan supérieur pour débloquer plus d'emplacements",
    vp_photos_copy_basic: "Basique / Gratuit — jusqu'à 2 photos · passez à Pro Premier pour 5, Elite VIP pour 10 ou Luxury Concierge pour 15.",
    vp_photos_copy_pro: "Pro Premier — jusqu'à 5 photos · passez à Elite VIP pour 10 (ou Luxury Concierge pour 15).",
    vp_photos_copy_elite: "Elite VIP — galerie de 10 photos · visibilité étendue · passez à Luxury pour 15.",
    vp_photos_copy_luxury: "Luxury Concierge — fiche premium · galerie de 15 photos · meilleur positionnement.",
    vp_photos_unlock_more: "Débloquer plus de photos →",
    vp_video_portfolio: "Portfolio vidéo",
    vp_video_elite_exclusive: "Exclusivité Elite",
    vp_video_add: "Ajoutez une vidéo de votre travail",
    vp_video_up_to_2min: "Jusqu'à 2 minutes",
    vp_video_locked_title: "Portfolio vidéo",
    vp_video_locked_desc: "Passez à Elite VIP pour ajouter une vidéo.",
    vp_video_locked_cta: "Débloquer",
    va_available: "Disponible",
    va_not_available: "Indisponible",
    va_visible_desc: "Vous êtes visible pour les clients et pouvez recevoir des réservations",
    va_hidden_desc: "Vous êtes masqué — pas de nouvelles réservations",
    va_go_offline: "Se déconnecter",
    va_go_online: "Se connecter",
    va_check_visible_searches: "Visible dans les recherches",
    va_check_receiving_bookings: "Réception des réservations",
    va_check_on_the_map: "Sur la carte",
    va_check_customer_notifications: "Notifications client",
    va_last_updated: "Dernière mise à jour : {time}",
    va_toggle_error: "Impossible de mettre à jour le statut. Veuillez réessayer.",
    vs_page_title: "Forfaits prestataire",
    vs_page_subtitle: "Passez à un forfait supérieur pour gagner plus, accéder en priorité aux réservations et vous démarquer.",
    vs_current_plan: "Forfait actuel",
    vs_commission_rate: "Commission",
    vs_benefit_earn_title: "Gagnez plus par mission",
    vs_benefit_earn_desc: "Des commissions plus basses signifient que vous gardez plus. Elite VIP économise jusqu'à 10 % par mission vs Standard.",
    vs_benefit_priority_title: "Accès prioritaire aux missions",
    vs_benefit_priority_desc: "Soyez le premier sur les meilleures missions. Les membres premium reçoivent les offres avant les prestataires standard.",
    vs_benefit_standout_title: "Démarquez-vous",
    vs_benefit_standout_desc: "Les badges premium et le placement vedette vous aident à attirer plus de clients et à instaurer la confiance.",
    vs_most_popular_badge: "Le plus choisi",
    vs_per_month: "/mois",
    vs_platform_fee_per_wash: "Commission · {pct} % par lavage",
    vs_select_plan: "Choisir",
    vs_current_plan_badge: "Forfait actuel",
  },

  pt: {
    vh_app_pending_title: "Candidatura em análise",
    vh_app_pending_desc: "A tua candidatura de fornecedor está em análise. Vamos avisar-te assim que estiver aprovada (geralmente 24–48 horas).",
    vh_stripe_pending_title: "Verificação Stripe em curso",
    vh_stripe_pending_desc: "Os teus dados foram enviados. A Stripe está a verificar a tua conta — normalmente uns minutos, até 24h. Podes aceitar e concluir reservas; os ganhos ficam em fila até a Stripe terminar.",
    vh_stripe_pending_refresh: "Atualizar",
    vh_stripe_warning_title: "Termina a configuração de pagamentos Stripe",
    vh_stripe_warning_desc: "Os pagamentos só serão libertados quando a configuração Stripe estiver concluída.",
    vh_stripe_warning_cta: "Terminar",
    vh_welcome_back: "Bem-vindo de volta,",
    vh_signed_in_as: "Sessão iniciada como {name}",
    vh_next_best_action_label: "Próxima ação recomendada",
    vh_action_profile_title: "Completa o teu perfil",
    vh_action_profile_desc: "Adiciona o nome do negócio e uma bio para os clientes te encontrarem e confiarem.",
    vh_action_profile_cta: "Completar perfil",
    vh_action_photos_title: "Adiciona as tuas primeiras fotos",
    vh_action_photos_desc: "Perfis com fotos recebem até 5× mais reservas. Carrega pelo menos uma.",
    vh_action_photos_cta: "Carregar fotos",
    vh_action_equipment_title: "Melhora a tua prontidão",
    vh_action_equipment_desc: "Estás em {pct}%. Atinge 80%+ para acesso prioritário a despacho.",
    vh_action_equipment_cta: "Abrir lista",
    vh_action_bookings_title: "{count} oferta à espera",
    vh_action_bookings_desc: "Responde rápido — as ofertas são reatribuídas após uns minutos.",
    vh_action_bookings_cta: "Ver ofertas",
    vh_action_upgrade_title: "Sobe de plano para reter mais ganhos",
    vh_action_upgrade_desc: "Os fornecedores Pro Premier mantêm 85% de cada lavagem e têm posicionamento prioritário no mapa.",
    vh_action_upgrade_cta: "Ver planos",
    vh_action_allset_title: "Está tudo pronto",
    vh_action_allset_desc: "Perfil, fotos e equipamento estão impecáveis. Continua o serviço 5 estrelas.",
    vh_action_allset_cta: "Ver trabalhos",
    vh_group_setup_title: "Configuração e ferramentas",
    vh_group_setup_subtitle: "Prepara-te para trabalhar",
    vh_group_grow_title: "Faz crescer o teu negócio",
    vh_group_grow_subtitle: "Mais trabalhos, melhor rota, maiores pagamentos",
    vh_link_equipment_title: "Lista de equipamento",
    vh_link_equipment_desc: "Acompanha o nível de prontidão",
    vh_link_training_title: "Formação e tutoriais",
    vh_link_training_desc: "Aprende a dar serviço 5 estrelas",
    vh_link_listing_title: "Pré-visualização do anúncio",
    vh_link_listing_desc: "Vê como os clientes te veem",
    vh_link_referrals_title: "Programa de indicações",
    vh_link_referrals_desc: "Ganha $50 por cada fornecedor que indicares",
    vh_link_schedule_title: "Agenda de hoje",
    vh_link_schedule_desc: "Vista cronológica das reservas de hoje",
    vh_link_route_title: "Rota inteligente",
    vh_link_route_desc: "Otimiza a ordem, poupa tempo e combustível",
    vp_header_subtitle_elite: "Máxima visibilidade e menores comissões",
    vp_header_subtitle_pro: "Visibilidade e funcionalidades melhoradas",
    vp_header_subtitle_basic: "Anúncio básico — sobe de plano para mais funções",
    vp_header_subtitle_luxury: "Topo do mercado · comissão mais baixa",
    vp_upgrade_to_pro_premier: "Subir para Pro Premier",
    vp_upgrade_to_elite_vip: "Subir para Elite VIP",
    vp_upgrade_to_luxury: "Subir para Luxury Concierge",
    vp_platform_fee_label: "Comissão de {pct}%",
    vp_basic_fee_warning_title: "Estás a pagar 20% por lavagem",
    vp_basic_fee_warning_desc: "Sobe para Pro Premier (15%) ou Elite VIP (10%) para reter mais ganhos.",
    vp_recent_jobs_title: "Trabalhos recentes",
    vp_recent_jobs_more_hidden: "+{count} trabalhos ocultos",
    vp_recent_jobs_upgrade_link: "Sobe de plano para ver todos →",
    vp_listing_preview_section_title: "Pré-visualização do cliente",
    vp_visibility_max: "Visibilidade máxima",
    vp_visibility_enhanced: "Visibilidade melhorada",
    vp_visibility_limited: "Visibilidade limitada",
    vp_bio_label: "Bio do negócio",
    vp_bio_chars_count: "{count}/{max} caracteres",
    vp_bio_upgrade_hint: "Sobe para {max} caracteres",
    vp_bio_basic_lock: "Sobe para Pro Premier para uma bio mais longa e detalhada que atrai mais clientes.",
    vp_work_photos_label: "Fotos de trabalho",
    vp_photos_count_label: "{count}/{max} fotos",
    vp_add_photo: "Adicionar foto",
    vp_uploading: "A carregar…",
    vp_remove_photo: "Remover foto",
    vp_photos_locked_aria: "Sobe de plano para desbloquear mais espaços",
    vp_photos_copy_basic: "Básico / Grátis — até 2 fotos · sobe para Pro Premier para 5, Elite VIP para 10 ou Luxury Concierge para 15.",
    vp_photos_copy_pro: "Pro Premier — até 5 fotos · sobe para Elite VIP para 10 (ou Luxury Concierge para 15).",
    vp_photos_copy_elite: "Elite VIP — galeria de 10 fotos · visibilidade alargada · sobe para Luxury para 15.",
    vp_photos_copy_luxury: "Luxury Concierge — anúncio premium · galeria de 15 fotos · melhor posicionamento.",
    vp_photos_unlock_more: "Desbloquear mais fotos →",
    vp_video_portfolio: "Portefólio em vídeo",
    vp_video_elite_exclusive: "Exclusivo Elite",
    vp_video_add: "Adiciona um vídeo do teu trabalho",
    vp_video_up_to_2min: "Até 2 minutos",
    vp_video_locked_title: "Portefólio em vídeo",
    vp_video_locked_desc: "Sobe para Elite VIP para adicionar vídeo.",
    vp_video_locked_cta: "Desbloquear",
    va_available: "Disponível",
    va_not_available: "Indisponível",
    va_visible_desc: "Estás visível para os clientes e podes receber reservas",
    va_hidden_desc: "Estás oculto nas pesquisas — sem novas reservas",
    va_go_offline: "Ficar offline",
    va_go_online: "Ficar online",
    va_check_visible_searches: "Visível nas pesquisas",
    va_check_receiving_bookings: "A receber reservas",
    va_check_on_the_map: "No mapa",
    va_check_customer_notifications: "Notificações do cliente",
    va_last_updated: "Última atualização: {time}",
    va_toggle_error: "Não foi possível atualizar o estado. Tenta novamente.",
    vs_page_title: "Planos de subscrição para fornecedores",
    vs_page_subtitle: "Sobe de plano para ganhar mais por trabalho, ter acesso prioritário a reservas e destacar-te entre os clientes.",
    vs_current_plan: "Plano atual",
    vs_commission_rate: "Comissão",
    vs_benefit_earn_title: "Ganha mais por trabalho",
    vs_benefit_earn_desc: "Comissões mais baixas significam que ficas com mais. Os Elite VIP poupam até 10% por trabalho vs Standard.",
    vs_benefit_priority_title: "Acesso prioritário a trabalhos",
    vs_benefit_priority_desc: "Sê o primeiro nos melhores trabalhos. Os membros premium recebem ofertas antes dos fornecedores standard.",
    vs_benefit_standout_title: "Destaca-te perante os clientes",
    vs_benefit_standout_desc: "Selos premium e destaque ajudam-te a atrair mais clientes e ganhar confiança.",
    vs_most_popular_badge: "Mais popular",
    vs_per_month: "/mês",
    vs_platform_fee_per_wash: "Comissão · {pct}% por lavagem",
    vs_select_plan: "Escolher plano",
    vs_current_plan_badge: "Plano atual",
  },

  de: {
    vh_app_pending_title: "Bewerbung in Prüfung",
    vh_app_pending_desc: "Deine Anbieter-Bewerbung wird geprüft. Wir benachrichtigen dich, sobald sie freigegeben ist (in der Regel 24–48 Stunden).",
    vh_stripe_pending_title: "Stripe-Verifizierung läuft",
    vh_stripe_pending_desc: "Deine Daten wurden übermittelt. Stripe prüft dein Konto — meist wenige Minuten, manchmal bis zu 24 Std. Du kannst jetzt Buchungen annehmen und abschließen; Auszahlungen warten bis zur Freigabe.",
    vh_stripe_pending_refresh: "Aktualisieren",
    vh_stripe_warning_title: "Stripe-Auszahlungs-Einrichtung abschließen",
    vh_stripe_warning_desc: "Auszahlungen erfolgen erst, wenn das Stripe-Onboarding abgeschlossen ist.",
    vh_stripe_warning_cta: "Abschließen",
    vh_welcome_back: "Willkommen zurück,",
    vh_signed_in_as: "Angemeldet als {name}",
    vh_next_best_action_label: "Nächster empfohlener Schritt",
    vh_action_profile_title: "Profil vervollständigen",
    vh_action_profile_desc: "Füge Firmennamen und Bio hinzu, damit Kunden dich finden und dir vertrauen.",
    vh_action_profile_cta: "Profil vervollständigen",
    vh_action_photos_title: "Erste Fotos hinzufügen",
    vh_action_photos_desc: "Profile mit Fotos erhalten bis zu 5× mehr Buchungen. Lade mindestens eines hoch.",
    vh_action_photos_cta: "Fotos hochladen",
    vh_action_equipment_title: "Ausrüstungs-Bereitschaft erhöhen",
    vh_action_equipment_desc: "Du bist bei {pct}%. Erreiche 80%+ für priorisierte Aufträge.",
    vh_action_equipment_cta: "Checkliste öffnen",
    vh_action_bookings_title: "{count} Buchungsangebot wartet",
    vh_action_bookings_desc: "Antworte schnell — Angebote werden nach wenigen Minuten neu zugewiesen.",
    vh_action_bookings_cta: "Angebote prüfen",
    vh_action_upgrade_title: "Upgrade — mehr von deinen Einnahmen behalten",
    vh_action_upgrade_desc: "Pro-Premier-Anbieter behalten 85% jeder Wäsche und werden bevorzugt platziert.",
    vh_action_upgrade_cta: "Tarife ansehen",
    vh_action_allset_title: "Alles bereit",
    vh_action_allset_desc: "Profil, Fotos und Ausrüstung sehen super aus. Liefere weiter 5-Sterne-Service.",
    vh_action_allset_cta: "Aufträge ansehen",
    vh_group_setup_title: "Einrichtung & Tools",
    vh_group_setup_subtitle: "Mach dich startklar",
    vh_group_grow_title: "Geschäft ausbauen",
    vh_group_grow_subtitle: "Mehr Aufträge, bessere Routen, mehr Auszahlung",
    vh_link_equipment_title: "Ausrüstungs-Checkliste",
    vh_link_equipment_desc: "Verfolge deine Bereitschaft",
    vh_link_training_title: "Schulung & Tutorials",
    vh_link_training_desc: "5-Sterne-Service liefern lernen",
    vh_link_listing_title: "Vorschau deines Eintrags",
    vh_link_listing_desc: "So sehen dich Kunden",
    vh_link_referrals_title: "Empfehlungsprogramm",
    vh_link_referrals_desc: "Verdiene 50 $ pro empfohlenem Anbieter",
    vh_link_schedule_title: "Heutiger Zeitplan",
    vh_link_schedule_desc: "Zeitachsen-Ansicht der heutigen Buchungen",
    vh_link_route_title: "Smart-Route",
    vh_link_route_desc: "Reihenfolge optimieren, Zeit und Sprit sparen",
    vp_header_subtitle_elite: "Maximale Sichtbarkeit und niedrigste Gebühren",
    vp_header_subtitle_pro: "Erweiterte Sichtbarkeit und Funktionen",
    vp_header_subtitle_basic: "Basis-Eintrag — Upgrade für mehr Funktionen",
    vp_header_subtitle_luxury: "Top des Marktes · niedrigste Provision",
    vp_upgrade_to_pro_premier: "Upgrade auf Pro Premier",
    vp_upgrade_to_elite_vip: "Upgrade auf Elite VIP",
    vp_upgrade_to_luxury: "Upgrade auf Luxury Concierge",
    vp_platform_fee_label: "{pct}% Plattformgebühr",
    vp_basic_fee_warning_title: "Du zahlst 20% pro Wäsche",
    vp_basic_fee_warning_desc: "Upgrade auf Pro Premier (15%) oder Elite VIP (10%) und behalte mehr.",
    vp_recent_jobs_title: "Letzte Aufträge",
    vp_recent_jobs_more_hidden: "+{count} weitere Aufträge ausgeblendet",
    vp_recent_jobs_upgrade_link: "Upgrade, um alles zu sehen →",
    vp_listing_preview_section_title: "Kundenansicht",
    vp_visibility_max: "Maximale Sichtbarkeit",
    vp_visibility_enhanced: "Erweiterte Sichtbarkeit",
    vp_visibility_limited: "Eingeschränkte Sichtbarkeit",
    vp_bio_label: "Unternehmens-Bio",
    vp_bio_chars_count: "{count}/{max} Zeichen",
    vp_bio_upgrade_hint: "Upgrade für {max} Zeichen",
    vp_bio_basic_lock: "Upgrade auf Pro Premier für eine längere, ausführlichere Bio, die mehr Kunden anzieht.",
    vp_work_photos_label: "Arbeitsfotos",
    vp_photos_count_label: "{count}/{max} Fotos",
    vp_add_photo: "Foto hinzufügen",
    vp_uploading: "Wird hochgeladen…",
    vp_remove_photo: "Foto entfernen",
    vp_photos_locked_aria: "Upgrade, um mehr Foto-Slots freizuschalten",
    vp_photos_copy_basic: "Basis / Kostenlos — bis zu 2 Fotos · Upgrade auf Pro Premier für 5, Elite VIP für 10 oder Luxury Concierge für 15.",
    vp_photos_copy_pro: "Pro Premier — bis zu 5 Fotos · Upgrade auf Elite VIP für 10 (oder Luxury Concierge für 15).",
    vp_photos_copy_elite: "Elite VIP — 10-Foto-Galerie · erweiterte Sichtbarkeit · Upgrade auf Luxury für 15.",
    vp_photos_copy_luxury: "Luxury Concierge — Premium-Eintrag · 15-Foto-Galerie · höchste Platzierung.",
    vp_photos_unlock_more: "Mehr Fotos freischalten →",
    vp_video_portfolio: "Video-Portfolio",
    vp_video_elite_exclusive: "Elite-exklusiv",
    vp_video_add: "Video deiner Arbeit hinzufügen",
    vp_video_up_to_2min: "Bis zu 2 Minuten",
    vp_video_locked_title: "Video-Portfolio",
    vp_video_locked_desc: "Upgrade auf Elite VIP, um Video hinzuzufügen.",
    vp_video_locked_cta: "Freischalten",
    va_available: "Verfügbar",
    va_not_available: "Nicht verfügbar",
    va_visible_desc: "Du bist für Kunden sichtbar und kannst Buchungen erhalten",
    va_hidden_desc: "Du bist in der Suche ausgeblendet — keine neuen Buchungen",
    va_go_offline: "Offline gehen",
    va_go_online: "Online gehen",
    va_check_visible_searches: "Sichtbar in der Suche",
    va_check_receiving_bookings: "Buchungen aktiv",
    va_check_on_the_map: "Auf der Karte",
    va_check_customer_notifications: "Kundenbenachrichtigungen",
    va_last_updated: "Zuletzt aktualisiert: {time}",
    va_toggle_error: "Status konnte nicht aktualisiert werden. Bitte erneut versuchen.",
    vs_page_title: "Anbieter-Abonnements",
    vs_page_subtitle: "Upgrade deinen Tarif, um pro Auftrag mehr zu verdienen, vorrangig auf Buchungen zuzugreifen und bei Kunden hervorzustechen.",
    vs_current_plan: "Aktueller Tarif",
    vs_commission_rate: "Provision",
    vs_benefit_earn_title: "Verdiene mehr pro Auftrag",
    vs_benefit_earn_desc: "Niedrigere Provisionen heißen, du behältst mehr. Elite VIP spart bis zu 10 % pro Auftrag im Vergleich zu Standard.",
    vs_benefit_priority_title: "Vorrang-Auftragszugang",
    vs_benefit_priority_desc: "Erhalte als Erster die besten Aufträge. Premium-Mitglieder bekommen Angebote vor Standard-Anbietern.",
    vs_benefit_standout_title: "Hebe dich ab",
    vs_benefit_standout_desc: "Premium-Badges und Hervorhebungen helfen, mehr Kunden zu gewinnen und Vertrauen aufzubauen.",
    vs_most_popular_badge: "Am beliebtesten",
    vs_per_month: "/Monat",
    vs_platform_fee_per_wash: "Provision · {pct} % pro Wäsche",
    vs_select_plan: "Tarif wählen",
    vs_current_plan_badge: "Aktueller Tarif",
  },

  zh: {
    vh_app_pending_title: "申请审核中",
    vh_app_pending_desc: "您的服务商申请正在审核中。通过后我们会立即通知您(通常 24–48 小时)。",
    vh_stripe_pending_title: "Stripe 验证进行中",
    vh_stripe_pending_desc: "您的资料已提交。Stripe 正在验证您的账户 — 通常需要几分钟,最多 24 小时。您现在可以接单并完成订单;收入将在 Stripe 完成验证后发放。",
    vh_stripe_pending_refresh: "刷新",
    vh_stripe_warning_title: "完成 Stripe 付款设置",
    vh_stripe_warning_desc: "在完成 Stripe 入驻前,款项不会发放。",
    vh_stripe_warning_cta: "完成",
    vh_welcome_back: "欢迎回来,",
    vh_signed_in_as: "已登录:{name}",
    vh_next_best_action_label: "下一步建议",
    vh_action_profile_title: "完善您的资料",
    vh_action_profile_desc: "添加公司名称与简介,让客户更容易找到并信任您。",
    vh_action_profile_cta: "完善资料",
    vh_action_photos_title: "添加您的第一张照片",
    vh_action_photos_desc: "有照片的资料预订量最多可提升 5 倍。先上传一张吧。",
    vh_action_photos_cta: "上传照片",
    vh_action_equipment_title: "提升装备就绪度",
    vh_action_equipment_desc: "您当前 {pct}%。达到 80%+ 即可获得优先派单。",
    vh_action_equipment_cta: "打开清单",
    vh_action_bookings_title: "{count} 个待处理订单",
    vh_action_bookings_desc: "请尽快响应 — 订单在几分钟后会自动重新分配。",
    vh_action_bookings_cta: "查看订单",
    vh_action_upgrade_title: "升级以保留更多收入",
    vh_action_upgrade_desc: "Pro Premier 服务商每单可保留 85%,并在地图上获得优先展示。",
    vh_action_upgrade_cta: "查看套餐",
    vh_action_allset_title: "一切就绪",
    vh_action_allset_desc: "资料、照片与装备都很棒,继续保持五星服务。",
    vh_action_allset_cta: "查看任务",
    vh_group_setup_title: "设置与工具",
    vh_group_setup_subtitle: "做好出发准备",
    vh_group_grow_title: "拓展业务",
    vh_group_grow_subtitle: "更多任务、更优路线、更高收入",
    vh_link_equipment_title: "装备清单",
    vh_link_equipment_desc: "追踪您的就绪度",
    vh_link_training_title: "培训与教程",
    vh_link_training_desc: "学习提供五星服务",
    vh_link_listing_title: "您的列表预览",
    vh_link_listing_desc: "查看客户看到的样子",
    vh_link_referrals_title: "推荐计划",
    vh_link_referrals_desc: "每推荐一位服务商即可获得 $50",
    vh_link_schedule_title: "今日日程",
    vh_link_schedule_desc: "今日订单时间轴",
    vh_link_route_title: "智能路线",
    vh_link_route_desc: "优化顺序,节省时间与油费",
    vp_header_subtitle_elite: "最大曝光与最低手续费",
    vp_header_subtitle_pro: "增强曝光与功能",
    vp_header_subtitle_basic: "基础列表 — 升级解锁更多功能",
    vp_header_subtitle_luxury: "市场顶部 · 最低佣金",
    vp_upgrade_to_pro_premier: "升级到 Pro Premier",
    vp_upgrade_to_elite_vip: "升级到 Elite VIP",
    vp_upgrade_to_luxury: "升级到 Luxury Concierge",
    vp_platform_fee_label: "平台佣金 {pct}%",
    vp_basic_fee_warning_title: "您每单支付 20%",
    vp_basic_fee_warning_desc: "升级到 Pro Premier (15%) 或 Elite VIP (10%) 以保留更多收入。",
    vp_recent_jobs_title: "近期任务",
    vp_recent_jobs_more_hidden: "另有 {count} 个任务已隐藏",
    vp_recent_jobs_upgrade_link: "升级以查看全部 →",
    vp_listing_preview_section_title: "客户视图预览",
    vp_visibility_max: "最大曝光",
    vp_visibility_enhanced: "增强曝光",
    vp_visibility_limited: "曝光有限",
    vp_bio_label: "业务简介",
    vp_bio_chars_count: "{count}/{max} 字符",
    vp_bio_upgrade_hint: "升级以获得 {max} 字符",
    vp_bio_basic_lock: "升级到 Pro Premier 获得更长更详细的简介,吸引更多客户。",
    vp_work_photos_label: "工作照片",
    vp_photos_count_label: "{count}/{max} 张照片",
    vp_add_photo: "添加照片",
    vp_uploading: "上传中…",
    vp_remove_photo: "移除照片",
    vp_photos_locked_aria: "升级以解锁更多照片位",
    vp_photos_copy_basic: "基础 / 免费 — 最多 2 张 · 升级 Pro Premier 享 5 张,Elite VIP 享 10 张,Luxury Concierge 享 15 张。",
    vp_photos_copy_pro: "Pro Premier — 最多 5 张 · 升级 Elite VIP 享 10 张(或 Luxury Concierge 享 15 张)。",
    vp_photos_copy_elite: "Elite VIP — 10 张照片图库 · 扩展曝光 · 升级 Luxury 享 15 张。",
    vp_photos_copy_luxury: "Luxury Concierge — 顶级列表 · 15 张照片图库 · 市场最高定位。",
    vp_photos_unlock_more: "解锁更多照片 →",
    vp_video_portfolio: "视频作品",
    vp_video_elite_exclusive: "Elite 专享",
    vp_video_add: "添加展示您工作的视频",
    vp_video_up_to_2min: "最长 2 分钟",
    vp_video_locked_title: "视频作品",
    vp_video_locked_desc: "升级到 Elite VIP 即可添加视频。",
    vp_video_locked_cta: "解锁",
    va_available: "可接单",
    va_not_available: "暂不接单",
    va_visible_desc: "客户可看到您,可接收预订",
    va_hidden_desc: "您在搜索中隐藏 — 无新预订",
    va_go_offline: "下线",
    va_go_online: "上线",
    va_check_visible_searches: "搜索中可见",
    va_check_receiving_bookings: "可接收预订",
    va_check_on_the_map: "显示在地图",
    va_check_customer_notifications: "客户通知",
    va_last_updated: "最近更新:{time}",
    va_toggle_error: "更新状态失败,请重试。",
    vs_page_title: "服务商订阅套餐",
    vs_page_subtitle: "升级套餐,提升每单收入,优先获得预订,在客户中脱颖而出。",
    vs_current_plan: "当前套餐",
    vs_commission_rate: "佣金率",
    vs_benefit_earn_title: "每单赚更多",
    vs_benefit_earn_desc: "更低的佣金意味着您保留更多收入。Elite VIP 会员相比 Standard 每单最多节省 10%。",
    vs_benefit_priority_title: "优先获取订单",
    vs_benefit_priority_desc: "优先获取最佳订单。高级会员先于标准服务商收到订单。",
    vs_benefit_standout_title: "在客户中脱颖而出",
    vs_benefit_standout_desc: "高级徽章与精选展示帮助您吸引更多客户、建立信任。",
    vs_most_popular_badge: "最受欢迎",
    vs_per_month: "/月",
    vs_platform_fee_per_wash: "平台佣金 · 每单 {pct}%",
    vs_select_plan: "选择套餐",
    vs_current_plan_badge: "当前套餐",
  },

  ja: {
    vh_app_pending_title: "申請審査中",
    vh_app_pending_desc: "ベンダー申請を審査中です。承認後すぐにお知らせします(通常 24〜48 時間)。",
    vh_stripe_pending_title: "Stripe 認証中",
    vh_stripe_pending_desc: "情報を送信しました。Stripe がアカウントを認証中 — 通常数分、最長 24 時間。今すぐ予約の受諾・完了が可能で、収益は認証完了まで保留されます。",
    vh_stripe_pending_refresh: "更新",
    vh_stripe_warning_title: "Stripe 出金設定を完了",
    vh_stripe_warning_desc: "Stripe 設定が完了するまで出金は実行されません。",
    vh_stripe_warning_cta: "完了する",
    vh_welcome_back: "おかえりなさい、",
    vh_signed_in_as: "{name} としてログイン中",
    vh_next_best_action_label: "次のおすすめアクション",
    vh_action_profile_title: "プロフィールを完成させる",
    vh_action_profile_desc: "事業名と紹介文を追加して、お客様に見つけてもらい、信頼を得ましょう。",
    vh_action_profile_cta: "プロフィールを完成",
    vh_action_photos_title: "最初の写真を追加",
    vh_action_photos_desc: "写真ありのプロフィールは最大 5 倍の予約を獲得します。まず 1 枚アップロードしましょう。",
    vh_action_photos_cta: "写真をアップロード",
    vh_action_equipment_title: "備品の準備度を上げる",
    vh_action_equipment_desc: "現在 {pct}%。80% 以上で優先配車対象になります。",
    vh_action_equipment_cta: "チェックリストを開く",
    vh_action_bookings_title: "{count} 件の予約オファーが待機中",
    vh_action_bookings_desc: "オファーは数分で自動再割当されます。お早めに対応を。",
    vh_action_bookings_cta: "オファーを確認",
    vh_action_upgrade_title: "アップグレードで収益をより多く確保",
    vh_action_upgrade_desc: "Pro Premier は毎回の洗車で 85% を確保し、マップで優先表示されます。",
    vh_action_upgrade_cta: "プランを見る",
    vh_action_allset_title: "準備万端!",
    vh_action_allset_desc: "プロフィール・写真・備品はバッチリ。5 つ星サービスを続けましょう。",
    vh_action_allset_cta: "案件を見る",
    vh_group_setup_title: "セットアップとツール",
    vh_group_setup_subtitle: "出発の準備をしましょう",
    vh_group_grow_title: "ビジネスを成長させる",
    vh_group_grow_subtitle: "案件増加・最適ルート・高収益",
    vh_link_equipment_title: "備品チェックリスト",
    vh_link_equipment_desc: "準備度を確認",
    vh_link_training_title: "トレーニングとチュートリアル",
    vh_link_training_desc: "5 つ星サービスを学ぶ",
    vh_link_listing_title: "出品プレビュー",
    vh_link_listing_desc: "お客様から見た自分を確認",
    vh_link_referrals_title: "紹介プログラム",
    vh_link_referrals_desc: "ベンダーを紹介すると 1 件あたり $50",
    vh_link_schedule_title: "今日の予定",
    vh_link_schedule_desc: "今日の予約タイムライン",
    vh_link_route_title: "スマートルート",
    vh_link_route_desc: "順序を最適化し、時間と燃料を節約",
    vp_header_subtitle_elite: "最大の露出と最低の手数料",
    vp_header_subtitle_pro: "露出と機能を強化",
    vp_header_subtitle_basic: "ベーシック — アップグレードで機能解放",
    vp_header_subtitle_luxury: "マーケット最上位 · 最低手数料",
    vp_upgrade_to_pro_premier: "Pro Premier にアップグレード",
    vp_upgrade_to_elite_vip: "Elite VIP にアップグレード",
    vp_upgrade_to_luxury: "Luxury Concierge にアップグレード",
    vp_platform_fee_label: "手数料 {pct}%",
    vp_basic_fee_warning_title: "毎回 20% の手数料を支払っています",
    vp_basic_fee_warning_desc: "Pro Premier(15%)または Elite VIP(10%)にアップグレードして手数料を抑えましょう。",
    vp_recent_jobs_title: "最近の案件",
    vp_recent_jobs_more_hidden: "+ {count} 件が非表示",
    vp_recent_jobs_upgrade_link: "すべて見るにはアップグレード →",
    vp_listing_preview_section_title: "顧客プレビュー",
    vp_visibility_max: "最大の露出",
    vp_visibility_enhanced: "強化された露出",
    vp_visibility_limited: "限定的な露出",
    vp_bio_label: "事業紹介",
    vp_bio_chars_count: "{count}/{max} 文字",
    vp_bio_upgrade_hint: "アップグレードで {max} 文字",
    vp_bio_basic_lock: "Pro Premier にアップグレードして、より長く詳細な紹介で顧客を惹きつけましょう。",
    vp_work_photos_label: "作業写真",
    vp_photos_count_label: "{count}/{max} 枚",
    vp_add_photo: "写真を追加",
    vp_uploading: "アップロード中…",
    vp_remove_photo: "写真を削除",
    vp_photos_locked_aria: "アップグレードで写真スロットを解放",
    vp_photos_copy_basic: "ベーシック / 無料 — 最大 2 枚 · Pro Premier で 5 枚、Elite VIP で 10 枚、Luxury Concierge で 15 枚に。",
    vp_photos_copy_pro: "Pro Premier — 最大 5 枚 · Elite VIP で 10 枚(または Luxury Concierge で 15 枚)に。",
    vp_photos_copy_elite: "Elite VIP — 10 枚ギャラリー · 露出強化 · Luxury にアップグレードで 15 枚。",
    vp_photos_copy_luxury: "Luxury Concierge — プレミアム掲載 · 15 枚ギャラリー · 最上位の表示。",
    vp_photos_unlock_more: "もっと写真を追加 →",
    vp_video_portfolio: "動画ポートフォリオ",
    vp_video_elite_exclusive: "Elite 限定",
    vp_video_add: "作業を紹介する動画を追加",
    vp_video_up_to_2min: "最大 2 分",
    vp_video_locked_title: "動画ポートフォリオ",
    vp_video_locked_desc: "動画追加には Elite VIP へのアップグレードが必要です。",
    vp_video_locked_cta: "解放",
    va_available: "受付中",
    va_not_available: "受付停止",
    va_visible_desc: "お客様に表示され、予約を受け付けられます",
    va_hidden_desc: "検索で非表示 — 新規予約なし",
    va_go_offline: "オフラインにする",
    va_go_online: "オンラインにする",
    va_check_visible_searches: "検索に表示",
    va_check_receiving_bookings: "予約受付中",
    va_check_on_the_map: "地図に表示",
    va_check_customer_notifications: "顧客通知",
    va_last_updated: "最終更新: {time}",
    va_toggle_error: "ステータスの更新に失敗しました。もう一度お試しください。",
    vs_page_title: "ベンダー サブスクリプション プラン",
    vs_page_subtitle: "プランをアップグレードして、1 件あたりの収益を増やし、予約に優先アクセスし、顧客の目に留まりましょう。",
    vs_current_plan: "現在のプラン",
    vs_commission_rate: "手数料率",
    vs_benefit_earn_title: "1 件あたり多く稼ぐ",
    vs_benefit_earn_desc: "手数料が低い=取り分が多い。Elite VIP は Standard と比較して 1 件あたり最大 10% お得。",
    vs_benefit_priority_title: "案件への優先アクセス",
    vs_benefit_priority_desc: "最良の案件を最初に。プレミアム会員は標準ベンダーより先にオファーを受けます。",
    vs_benefit_standout_title: "顧客から際立つ",
    vs_benefit_standout_desc: "プレミアムバッジと注目掲載で、より多くの顧客を惹きつけ、信頼を築きます。",
    vs_most_popular_badge: "人気No.1",
    vs_per_month: "/月",
    vs_platform_fee_per_wash: "手数料 · 1 件 {pct}%",
    vs_select_plan: "プランを選ぶ",
    vs_current_plan_badge: "現在のプラン",
  },

  ko: {
    vh_app_pending_title: "신청 검토 중",
    vh_app_pending_desc: "벤더 신청이 검토 중입니다. 승인되면 바로 알려드립니다(보통 24~48시간).",
    vh_stripe_pending_title: "Stripe 인증 진행 중",
    vh_stripe_pending_desc: "정보가 제출되었습니다. Stripe가 계정을 확인 중이며 — 보통 몇 분, 최대 24시간 소요. 지금 예약을 수락·완료할 수 있고, 수익은 인증 완료 후 지급됩니다.",
    vh_stripe_pending_refresh: "새로고침",
    vh_stripe_warning_title: "Stripe 정산 설정 완료",
    vh_stripe_warning_desc: "Stripe 온보딩이 완료될 때까지 정산되지 않습니다.",
    vh_stripe_warning_cta: "완료",
    vh_welcome_back: "환영합니다,",
    vh_signed_in_as: "{name}님으로 로그인",
    vh_next_best_action_label: "다음 추천 작업",
    vh_action_profile_title: "프로필 완성하기",
    vh_action_profile_desc: "사업체 이름과 소개를 추가해 고객의 신뢰를 얻으세요.",
    vh_action_profile_cta: "프로필 완성",
    vh_action_photos_title: "첫 사진 추가",
    vh_action_photos_desc: "사진이 있는 프로필은 예약이 최대 5배 증가합니다. 한 장 이상 업로드하세요.",
    vh_action_photos_cta: "사진 업로드",
    vh_action_equipment_title: "장비 준비도 높이기",
    vh_action_equipment_desc: "현재 {pct}%. 80% 이상이면 우선 배차 대상이 됩니다.",
    vh_action_equipment_cta: "체크리스트 열기",
    vh_action_bookings_title: "{count}건의 예약 대기 중",
    vh_action_bookings_desc: "오퍼는 몇 분 후 자동 재배정됩니다. 빠르게 응답하세요.",
    vh_action_bookings_cta: "오퍼 확인",
    vh_action_upgrade_title: "업그레이드로 더 많이 받기",
    vh_action_upgrade_desc: "Pro Premier 벤더는 세차당 85%를 가져가고 지도에서 우선 노출됩니다.",
    vh_action_upgrade_cta: "요금제 보기",
    vh_action_allset_title: "모든 준비 완료",
    vh_action_allset_desc: "프로필·사진·장비가 모두 훌륭합니다. 5성 서비스를 계속하세요.",
    vh_action_allset_cta: "작업 보기",
    vh_group_setup_title: "설정 및 도구",
    vh_group_setup_subtitle: "출발 준비",
    vh_group_grow_title: "비즈니스 성장",
    vh_group_grow_subtitle: "더 많은 작업·최적 경로·더 큰 수익",
    vh_link_equipment_title: "장비 체크리스트",
    vh_link_equipment_desc: "준비도 추적",
    vh_link_training_title: "교육 및 튜토리얼",
    vh_link_training_desc: "5성 서비스 배우기",
    vh_link_listing_title: "내 리스팅 미리보기",
    vh_link_listing_desc: "고객이 보는 모습 확인",
    vh_link_referrals_title: "추천 프로그램",
    vh_link_referrals_desc: "벤더 추천당 $50 적립",
    vh_link_schedule_title: "오늘의 일정",
    vh_link_schedule_desc: "오늘 예약의 타임라인 보기",
    vh_link_route_title: "스마트 경로",
    vh_link_route_desc: "순서를 최적화해 시간·연료 절약",
    vp_header_subtitle_elite: "최대 노출과 최저 수수료",
    vp_header_subtitle_pro: "강화된 노출과 기능",
    vp_header_subtitle_basic: "기본 리스팅 — 업그레이드로 기능 해제",
    vp_header_subtitle_luxury: "마켓플레이스 상단 · 최저 수수료",
    vp_upgrade_to_pro_premier: "Pro Premier로 업그레이드",
    vp_upgrade_to_elite_vip: "Elite VIP로 업그레이드",
    vp_upgrade_to_luxury: "Luxury Concierge로 업그레이드",
    vp_platform_fee_label: "플랫폼 수수료 {pct}%",
    vp_basic_fee_warning_title: "세차당 20%를 내고 있습니다",
    vp_basic_fee_warning_desc: "Pro Premier(15%) 또는 Elite VIP(10%)로 업그레이드하고 수익을 더 챙기세요.",
    vp_recent_jobs_title: "최근 작업",
    vp_recent_jobs_more_hidden: "+{count}건 숨김",
    vp_recent_jobs_upgrade_link: "업그레이드하고 모두 보기 →",
    vp_listing_preview_section_title: "고객 미리보기",
    vp_visibility_max: "최대 노출",
    vp_visibility_enhanced: "강화된 노출",
    vp_visibility_limited: "제한된 노출",
    vp_bio_label: "사업 소개",
    vp_bio_chars_count: "{count}/{max}자",
    vp_bio_upgrade_hint: "업그레이드로 {max}자",
    vp_bio_basic_lock: "Pro Premier로 업그레이드해 더 길고 자세한 소개로 고객을 사로잡으세요.",
    vp_work_photos_label: "작업 사진",
    vp_photos_count_label: "{count}/{max}장",
    vp_add_photo: "사진 추가",
    vp_uploading: "업로드 중…",
    vp_remove_photo: "사진 삭제",
    vp_photos_locked_aria: "업그레이드로 사진 슬롯 잠금 해제",
    vp_photos_copy_basic: "베이직 / 무료 — 최대 2장 · Pro Premier 5장, Elite VIP 10장, Luxury Concierge 15장으로 업그레이드.",
    vp_photos_copy_pro: "Pro Premier — 최대 5장 · Elite VIP 10장(또는 Luxury Concierge 15장)으로 업그레이드.",
    vp_photos_copy_elite: "Elite VIP — 10장 갤러리 · 강화된 노출 · Luxury로 업그레이드해 15장.",
    vp_photos_copy_luxury: "Luxury Concierge — 프리미엄 리스팅 · 15장 갤러리 · 최상위 노출.",
    vp_photos_unlock_more: "사진 더 잠금 해제 →",
    vp_video_portfolio: "영상 포트폴리오",
    vp_video_elite_exclusive: "Elite 전용",
    vp_video_add: "작업 소개 영상 추가",
    vp_video_up_to_2min: "최대 2분",
    vp_video_locked_title: "영상 포트폴리오",
    vp_video_locked_desc: "영상 추가는 Elite VIP 업그레이드가 필요합니다.",
    vp_video_locked_cta: "해제",
    va_available: "수락 가능",
    va_not_available: "수락 불가",
    va_visible_desc: "고객에게 노출되며 예약을 받을 수 있습니다",
    va_hidden_desc: "검색에서 숨김 — 신규 예약 없음",
    va_go_offline: "오프라인 전환",
    va_go_online: "온라인 전환",
    va_check_visible_searches: "검색 노출",
    va_check_receiving_bookings: "예약 수신 중",
    va_check_on_the_map: "지도에 표시",
    va_check_customer_notifications: "고객 알림",
    va_last_updated: "마지막 업데이트: {time}",
    va_toggle_error: "상태 업데이트 실패. 다시 시도해 주세요.",
    vs_page_title: "벤더 구독 플랜",
    vs_page_subtitle: "플랜을 업그레이드해 작업당 더 많이 벌고, 예약 우선 접근권을 얻고, 고객에게 돋보이세요.",
    vs_current_plan: "현재 플랜",
    vs_commission_rate: "수수료율",
    vs_benefit_earn_title: "작업당 수익 증가",
    vs_benefit_earn_desc: "낮은 수수료=더 큰 수익. Elite VIP는 Standard 대비 작업당 최대 10% 절약.",
    vs_benefit_priority_title: "작업 우선 접근",
    vs_benefit_priority_desc: "최고의 작업을 먼저. 프리미엄 회원은 표준 벤더보다 먼저 오퍼를 받습니다.",
    vs_benefit_standout_title: "고객에게 돋보이기",
    vs_benefit_standout_desc: "프리미엄 배지와 추천 노출로 더 많은 고객을 끌고 신뢰를 쌓으세요.",
    vs_most_popular_badge: "가장 인기",
    vs_per_month: "/월",
    vs_platform_fee_per_wash: "수수료 · 작업당 {pct}%",
    vs_select_plan: "플랜 선택",
    vs_current_plan_badge: "현재 플랜",
  },
  // Phase i18n expansion 2026-06-12 — Italian added (was a P1 gap:
  // IT went LIVE in the picker without vendor-portal legacy keys).
  it: {
    "vh_app_pending_title": "Domanda in attesa di revisione",
    "vh_app_pending_desc": "La tua domanda di fornitore è in revisione. Ti informeremo una volta approvata (di solito entro 24-48 ore).",
    "vh_stripe_pending_title": "Verifica Stripe in corso",
    "vh_stripe_pending_desc": "I tuoi dati sono stati inviati. Stripe sta verificando il tuo account — solitamente richiede pochi minuti, ma a volte fino a 24 ore. Puoi già accettare e completare prenotazioni; i guadagni verranno accodati fino al termine della verifica da parte di Stripe.",
    "vh_stripe_pending_refresh": "Aggiorna",
    "vh_stripe_warning_title": "Completa la configurazione dei pagamenti Stripe",
    "vh_stripe_warning_desc": "I pagamenti non verranno rilasciati finché l'onboarding di Stripe non sarà completato.",
    "vh_stripe_warning_cta": "Completa",
    "vh_welcome_back": "Bentornato,",
    "vh_signed_in_as": "Connesso come {name}",
    "vh_next_best_action_label": "Prossima azione consigliata",
    "vh_action_profile_title": "Completa il tuo profilo",
    "vh_action_profile_desc": "Aggiungi il nome della tua attività e la descrizione per farti trovare e dare fiducia ai clienti.",
    "vh_action_profile_cta": "Completa profilo",
    "vh_action_photos_title": "Aggiungi le tue prime foto",
    "vh_action_photos_desc": "I profili con foto ricevono fino a 5 volte più prenotazioni. Caricane almeno una per iniziare.",
    "vh_action_photos_cta": "Carica foto",
    "vh_action_equipment_title": "Migliora la tua preparazione dell'attrezzatura",
    "vh_action_equipment_desc": "Sei al {pct}%. Raggiungi 80%+ per qualificarti per l'assegnazione prioritaria.",
    "vh_action_equipment_cta": "Apri elenco",
    "vh_action_bookings_title": "{count} offerta di prenotazione in attesa",
    "vh_action_bookings_desc": "Rispondi rapidamente — le offerte vengono riassegnate automaticamente dopo pochi minuti.",
    "vh_action_bookings_cta": "Esamina offerte",
    "vh_action_upgrade_title": "Passa di livello per conservare più guadagni",
    "vh_action_upgrade_desc": "I fornitori Pro Premier conservano l'85% di ogni lavaggio e ottengono piazzamenti prioritari sulla mappa.",
    "vh_action_upgrade_cta": "Vedi piani",
    "vh_action_allset_title": "Tutto pronto",
    "vh_action_allset_desc": "Profilo, foto e attrezzatura sono a posto. Continua a offrire un servizio a 5 stelle.",
    "vh_action_allset_cta": "Visualizza lavori",
    "vh_group_setup_title": "Impostazioni e strumenti",
    "vh_group_setup_subtitle": "Preparati per la consegna",
    "vh_group_grow_title": "Fai crescere la tua attività",
    "vh_group_grow_subtitle": "Più lavori, migliore instradamento, pagamenti più grandi",
    "vh_link_equipment_title": "Elenco attrezzatura",
    "vh_link_equipment_desc": "Segui la preparazione della tua attrezzatura",
    "vh_link_training_title": "Formazione e tutorial",
    "vh_link_training_desc": "Impara come offrire un servizio a 5 stelle",
    "vh_link_listing_title": "Anteprima del tuo annuncio",
    "vh_link_listing_desc": "Scopri come i clienti vedono il tuo profilo",
    "vh_link_referrals_title": "Programma di referral",
    "vh_link_referrals_desc": "Guadagna 50$ per ogni fornitore che presenti",
    "vh_link_schedule_title": "Programma di oggi",
    "vh_link_schedule_desc": "Vista della timeline delle prenotazioni di oggi",
    "vh_link_route_title": "Percorso intelligente",
    "vh_link_route_desc": "Ottimizza l'ordine di guida, risparmia tempo e carburante",
    "vp_header_subtitle_elite": "Massima visibilità e tariffe più basse",
    "vp_header_subtitle_pro": "Visibilità aumentata e funzionalità avanzate",
    "vp_header_subtitle_basic": "Annuncio base — aggiorna per più funzionalità",
    "vp_header_subtitle_luxury": "Posizionamento top sul marketplace · commissione più bassa",
    "vp_upgrade_to_pro_premier": "Passa a Pro Premier",
    "vp_upgrade_to_elite_vip": "Passa a Elite VIP",
    "vp_upgrade_to_luxury": "Passa a Luxury Concierge",
    "vp_platform_fee_label": "{pct}% Commissioni sulla piattaforma",
    "vp_basic_fee_warning_title": "Stai pagando il 20% per lavaggio",
    "vp_basic_fee_warning_desc": "Passa a Pro Premier (15%) o Elite VIP (10%) per conservare più guadagni.",
    "vp_recent_jobs_title": "Lavori recenti",
    "vp_recent_jobs_more_hidden": "+{count} lavori in più nascosti",
    "vp_recent_jobs_upgrade_link": "Passa di livello per vederli tutti →",
    "vp_listing_preview_section_title": "Anteprima cliente",
    "vp_visibility_max": "Visibilità massima",
    "vp_visibility_enhanced": "Visibilità migliorata",
    "vp_visibility_limited": "Visibilità limitata",
    "vp_bio_label": "Bio attività",
    "vp_bio_chars_count": "{count}/{max} caratteri",
    "vp_bio_upgrade_hint": "Passa di livello per {max} caratteri",
    "vp_bio_basic_lock": "Passa a Pro Premier per una bio più lunga e dettagliata che attira più clienti.",
    "vp_work_photos_label": "Foto dei lavori",
    "vp_photos_count_label": "{count}/{max} foto",
    "vp_add_photo": "Aggiungi foto",
    "vp_uploading": "Caricamento…",
    "vp_remove_photo": "Rimuovi foto",
    "vp_photos_locked_aria": "Passa di livello per sbloccare più spazi per foto",
    "vp_photos_copy_basic": "Basic / Gratis — fino a 2 foto · passa a Pro Premier per 5, Elite VIP per 10, o Luxury Concierge per 15.",
    "vp_photos_copy_pro": "Pro Premier — fino a 5 foto · passa a Elite VIP per 10 (o Luxury Concierge per 15).",
    "vp_photos_copy_elite": "Elite VIP — galleria con 10 foto · visibilità espansa e posizionamento prioritario · passa a Luxury per 15.",
    "vp_photos_copy_luxury": "Luxury Concierge — inserzione premium top · galleria con 15 foto · posizionamento massimo nel mercato.",
    "vp_photos_unlock_more": "Sblocca più foto →",
    "vp_video_portfolio": "Portfolio video",
    "vp_video_elite_exclusive": "Esclusiva Elite",
    "vp_video_add": "Aggiungi un video che mostri il tuo lavoro",
    "vp_video_up_to_2min": "Fino a 2 minuti",
    "vp_video_locked_title": "Portfolio video",
    "vp_video_locked_desc": "Passa a Elite VIP per aggiungere contenuti video.",
    "vp_video_locked_cta": "Sblocca",
    "va_available": "Disponibile",
    "va_not_available": "Non disponibile",
    "va_visible_desc": "Sei visibile ai clienti e puoi ricevere prenotazioni",
    "va_hidden_desc": "Sei nascosto dalle ricerche — nessuna nuova prenotazione",
    "va_go_offline": "Vai offline",
    "va_go_online": "Vai online",
    "va_check_visible_searches": "Visibile nelle ricerche",
    "va_check_receiving_bookings": "Ricezione prenotazioni",
    "va_check_on_the_map": "Sulla mappa",
    "va_check_customer_notifications": "Notifiche clienti",
    "va_last_updated": "Ultimo aggiornamento: {time}",
    "va_toggle_error": "Errore nell'aggiornamento dello stato di disponibilità. Riprovare.",
    "vs_page_title": "Piani di abbonamento venditore",
    "vs_page_subtitle": "Passa di livello per guadagnare di più per lavoro, ottenere accesso prioritario alle prenotazioni e distinguerti per i clienti.",
    "vs_current_plan": "Piano attuale",
    "vs_commission_rate": "Commissione",
    "vs_benefit_earn_title": "Guadagna di più per ogni lavoro",
    "vs_benefit_earn_desc": "Tariffe di commissione più basse significano che trattieni di più di ciò che guadagni. I membri Elite VIP risparmiano fino al 10% per lavoro rispetto allo Standard.",
    "vs_benefit_priority_title": "Accesso prioritario ai lavori",
    "vs_benefit_priority_desc": "Ottieni per primo le migliori offerte di lavoro. I membri Premium ricevono offerte prima dei venditori standard.",
    "vs_benefit_standout_title": "Distinguerti per i clienti",
    "vs_benefit_standout_desc": "Distintivi premium e posizionamento in evidenza ti aiutano ad attirare più clienti e a costruire fiducia.",
    "vs_most_popular_badge": "Più popolare",
    "vs_per_month": "/mese",
    "vs_platform_fee_per_wash": "Commissione piattaforma · {pct}% per lavaggio",
    "vs_select_plan": "Seleziona piano",
    "vs_current_plan_badge": "Piano attuale",
    "vs_platform_commission": "Commissione della piattaforma",
    "vs_monthly_label": "Mensile",
    "vs_current_plan_btn": "Piano attuale",
    "vs_upgrade_btn": "Aggiorna",
    "vs_switch_plan_btn": "Cambia piano",
    "vs_savings_card_title": "Scopri i tuoi risparmi",
    "vs_savings_card_desc": "Calcola quanto potresti guadagnare in più con un piano premium",
    "vs_savings_if_earn": "Se guadagni {amount}/mese",
    "vs_savings_standard_keep": "Standard: Tieni {amount}",
    "vs_savings_pro_keep": "Pro Premier: Tieni {amount}",
    "vs_savings_elite_keep": "Elite VIP: Tieni {amount}",
    "vs_faq_title": "Domande frequenti",
    "vs_faq_cancel_q": "Posso annullare in qualsiasi momento?",
    "vs_faq_cancel_a": "Sì! Puoi annullare l'abbonamento in qualsiasi momento. Continuerai ad avere accesso fino alla fine del periodo di fatturazione.",
    "vs_faq_commission_q": "Quando cambiano le tariffe di commissione?",
    "vs_faq_commission_a": "La tua nuova tariffa di commissione entra in vigore immediatamente dopo l'iscrizione. Tutti i lavori futuri utilizzeranno la tua nuova tariffa.",
    "vs_faq_payment_q": "Quali metodi di pagamento sono accettati?",
    "vs_faq_payment_a": "Accettiamo tutte le principali carte di credito attraverso il nostro processore di pagamento sicuro, Stripe.",
    "vs_toast_cancelled": "Abbonamento annullato. Ora sei nel piano Standard.",
    "vs_toast_failed": "L'elaborazione dell'abbonamento è fallita",
    "vs_toast_verify_timeout": "Verifica dell'abbonamento scaduta. Per favore aggiorna.",
    "vs_toast_welcome_to": "Benvenuto in {name}!",
    "vs_feat_basic_listing": "Inserimento base",
    "vs_feat_standard_commission": "Commissione standard (20%)",
    "vs_feat_access_offers": "Accesso a tutte le offerte di lavoro",
    "vs_feat_basic_support": "Supporto base",
    "vs_feat_priority_offers": "Offerte di lavoro prioritarie",
    "vs_feat_featured_placement": "Posizionamento in evidenza",
    "vs_feat_priority_support": "Supporto prioritario",
    "vs_feat_lower_commission": "Tariffe di commissione più basse",
    "vs_feat_priority_dispatch": "Priorità nella coda di assegnazione",
    "vs_feat_reduced_commission": "Commissione ridotta (15%)",
    "vs_feat_featured_search": "In evidenza nei risultati di ricerca",
    "vs_feat_priority_support_response": "Risposta supporto prioritario",
    "vs_feat_detailed_analytics": "Dashboard analitica dettagliata",
    "vs_feat_professional_badge": "Distintivo professionale sul profilo",
    "vs_feat_top_placement": "Posizionamento al top in tutte le ricerche",
    "vs_feat_lowest_commission": "Commissione più bassa (10%)",
    "vs_feat_first_access_premium": "Accesso prioritario ai lavori premium",
    "vs_feat_vip_support": "Linea di supporto dedicata VIP",
    "vs_feat_advanced_analytics": "Analisi avanzate e approfondimenti",
    "vs_feat_elite_badge": "Distintivo Elite sul profilo",
    "vs_feat_marketing_features": "Opportunità di funzionalità di marketing",
    "vs_feat_exclusive_events": "Accesso agli eventi esclusivi per fornitori",
    "vs_feat_top_placement_guarantee": "Garanzia di posizionamento migliore",
    "vs_feat_vip_dedicated_support": "Supporto dedicato VIP"
  },
  // Phase i18n expansion 2026-06-12 — Russian added (same gap as IT).
  ru: {
    "vh_app_pending_title": "Заявка на рассмотрении",
    "vh_app_pending_desc": "Ваша заявка поставщика находится на рассмотрении. Мы уведомим вас после её одобрения (обычно 24–48 часов).",
    "vh_stripe_pending_title": "Идёт проверка Stripe",
    "vh_stripe_pending_desc": "Ваши данные отправлены. Stripe проверяет ваш аккаунт — обычно это занимает несколько минут, иногда до 24 часов. Вы можете принимать и завершать бронирования уже сейчас; выплаты будут в очереди до завершения проверки Stripe.",
    "vh_stripe_pending_refresh": "Обновить",
    "vh_stripe_warning_title": "Завершите настройку выплат через Stripe",
    "vh_stripe_warning_desc": "Выплаты не будут произведены, пока не завершена настройка Stripe.",
    "vh_stripe_warning_cta": "Завершить",
    "vh_welcome_back": "С возвращением,",
    "vh_signed_in_as": "Вошли как {name}",
    "vh_next_best_action_label": "Следующее лучшее действие",
    "vh_action_profile_title": "Заполните ваш профиль",
    "vh_action_profile_desc": "Добавьте название бизнеса и биографию, чтобы клиенты могли вас найти и доверять.",
    "vh_action_profile_cta": "Заполнить профиль",
    "vh_action_photos_title": "Добавьте свои первые фото",
    "vh_action_photos_desc": "Профили с фотографиями получают до 5 раз больше заказов. Загрузите хотя бы одно, чтобы начать.",
    "vh_action_photos_cta": "Загрузить фото",
    "vh_action_equipment_title": "Улучшите готовность оборудования",
    "vh_action_equipment_desc": "Ваша готовность: {pct}%. Достигните 80%+, чтобы претендовать на приоритетную отправку.",
    "vh_action_equipment_cta": "Открыть чек-лист",
    "vh_action_bookings_title": "{count} предложение о бронировании ожидает",
    "vh_action_bookings_desc": "Реагируйте быстро — предложения переадресовываются автоматически через несколько минут.",
    "vh_action_bookings_cta": "Просмотреть предложения",
    "vh_action_upgrade_title": "Обновите, чтобы оставлять больше заработка у себя",
    "vh_action_upgrade_desc": "Поставщики Pro Premier сохраняют 85% от каждой мойки и получают приоритетное размещение на карте.",
    "vh_action_upgrade_cta": "Посмотреть планы",
    "vh_action_allset_title": "Всё готово",
    "vh_action_allset_desc": "Профиль, фото и оборудование выглядят отлично. Продолжайте предоставлять сервис на 5 звёзд.",
    "vh_action_allset_cta": "Просмотреть заказы",
    "vh_group_setup_title": "Настройка и инструменты",
    "vh_group_setup_subtitle": "Подготовьтесь к предоставлению услуг",
    "vh_group_grow_title": "Развивайте свой бизнес",
    "vh_group_grow_subtitle": "Больше заказов, лучшая маршрутизация, большие выплаты",
    "vh_link_equipment_title": "Чек-лист оборудования",
    "vh_link_equipment_desc": "Отслеживайте готовность вашего оборудования",
    "vh_link_training_title": "Обучение и учебные материалы",
    "vh_link_training_desc": "Узнайте, как предоставлять сервис на 5 звёзд",
    "vh_link_listing_title": "Предварительный просмотр вашего профиля",
    "vh_link_listing_desc": "Узнайте, как клиенты видят ваш профиль",
    "vh_link_referrals_title": "Реферальная программа",
    "vh_link_referrals_desc": "Заработайте $50 за каждого привлечённого поставщика",
    "vh_link_schedule_title": "Расписание на сегодня",
    "vh_link_schedule_desc": "Просмотр расписания бронирований на сегодня",
    "vh_link_route_title": "Умный маршрут",
    "vh_link_route_desc": "Оптимизируйте порядок маршрута, экономьте время и топливо",
    "vp_header_subtitle_elite": "Максимальная видимость и минимальные комиссии",
    "vp_header_subtitle_pro": "Улучшенная видимость и функции",
    "vp_header_subtitle_basic": "Базовое размещение — обновите для получения дополнительных функций",
    "vp_header_subtitle_luxury": "Топовое размещение на рынке · минимальная комиссия",
    "vp_upgrade_to_pro_premier": "Обновите до Pro Premier",
    "vp_upgrade_to_elite_vip": "Перейти на Elite VIP",
    "vp_upgrade_to_luxury": "Перейти на Luxury Concierge",
    "vp_platform_fee_label": "{pct}% комиссия платформы",
    "vp_basic_fee_warning_title": "Вы платите 20% за каждую мойку",
    "vp_basic_fee_warning_desc": "Перейдите на Pro Premier (15%) или Elite VIP (10%), чтобы сохранить больше заработка.",
    "vp_recent_jobs_title": "Недавние заказы",
    "vp_recent_jobs_more_hidden": "+{count} скрытых заказов",
    "vp_recent_jobs_upgrade_link": "Обновите план, чтобы увидеть все →",
    "vp_listing_preview_section_title": "Предварительный просмотр для клиента",
    "vp_visibility_max": "Максимальная видимость",
    "vp_visibility_enhanced": "Улучшенная видимость",
    "vp_visibility_limited": "Ограниченная видимость",
    "vp_bio_label": "О бизнесе",
    "vp_bio_chars_count": "{count}/{max} символов",
    "vp_bio_upgrade_hint": "Улучшите для {max} символов",
    "vp_bio_basic_lock": "Обновите до Pro Premier, чтобы сделать описание более длинным и подробным, привлекая больше клиентов.",
    "vp_work_photos_label": "Фото работ",
    "vp_photos_count_label": "{count}/{max} фото",
    "vp_add_photo": "Добавить фото",
    "vp_uploading": "Загрузка…",
    "vp_remove_photo": "Удалить фото",
    "vp_photos_locked_aria": "Обновите план, чтобы добавить больше фото",
    "vp_photos_copy_basic": "Базовый / Бесплатно — до 2 фото · обновите до Pro Premier для 5, Elite VIP для 10 или Luxury Concierge для 15.",
    "vp_photos_copy_pro": "Pro Premier — до 5 фото · обновите до Elite VIP для 10 (или Luxury Concierge для 15).",
    "vp_photos_copy_elite": "Elite VIP — галерея из 10 фото · расширенная видимость и приоритетное размещение · обновите до Luxury для 15.",
    "vp_photos_copy_luxury": "Luxury Concierge — топовое премиальное размещение · галерея из 15 фото · высшая позиция на маркетплейсе.",
    "vp_photos_unlock_more": "Добавьте больше фото →",
    "vp_video_portfolio": "Портфолио видео",
    "vp_video_elite_exclusive": "Эксклюзив для Elite",
    "vp_video_add": "Добавьте видео, показывающее вашу работу",
    "vp_video_up_to_2min": "До 2 минут",
    "vp_video_locked_title": "Портфолио видео",
    "vp_video_locked_desc": "Обновите до Elite VIP, чтобы добавить видеоконтент.",
    "vp_video_locked_cta": "Разблокировать",
    "va_available": "Доступен",
    "va_not_available": "Недоступен",
    "va_visible_desc": "Вы видимы для клиентов и можете получать заказы",
    "va_hidden_desc": "Вы скрыты из поиска — нет новых заказов",
    "va_go_offline": "Отключиться",
    "va_go_online": "Подключиться",
    "va_check_visible_searches": "Виден в поиске",
    "va_check_receiving_bookings": "Получение заказов",
    "va_check_on_the_map": "На карте",
    "va_check_customer_notifications": "Уведомления клиентов",
    "va_last_updated": "Последнее обновление: {time}",
    "va_toggle_error": "Не удалось обновить статус доступности. Пожалуйста, попробуйте снова.",
    "vs_page_title": "Тарифные планы для поставщиков",
    "vs_page_subtitle": "Обновите свой план, чтобы зарабатывать больше за заказ, получить приоритетный доступ к заказам и выделяться среди клиентов.",
    "vs_current_plan": "Текущий план",
    "vs_commission_rate": "Комиссия",
    "vs_benefit_earn_title": "Зарабатывайте больше за заказ",
    "vs_benefit_earn_desc": "Низкие комиссии означают, что вы сохраняете больше заработанного. Элитные VIP-члены экономят до 10% за заказ по сравнению со Стандартом.",
    "vs_benefit_priority_title": "Приоритетный доступ к заказам",
    "vs_benefit_priority_desc": "Первыми получайте доступ к лучшим заказам. Премиум-участники получают предложения раньше стандартных исполнителей.",
    "vs_benefit_standout_title": "Выделяйтесь среди клиентов",
    "vs_benefit_standout_desc": "Премиум-значки и выделенное размещение помогут привлечь больше клиентов и завоевать доверие.",
    "vs_most_popular_badge": "Самый популярный",
    "vs_per_month": "/месяц",
    "vs_platform_fee_per_wash": "Платеж за платформу · {pct}% за мойку",
    "vs_select_plan": "Выбрать план",
    "vs_current_plan_badge": "Текущий план",
    "vs_platform_commission": "Комиссия платформы",
    "vs_monthly_label": "Ежемесячно",
    "vs_current_plan_btn": "Текущий план",
    "vs_upgrade_btn": "Обновить",
    "vs_switch_plan_btn": "Переключить план",
    "vs_savings_card_title": "Посмотрите ваши сбережения",
    "vs_savings_card_desc": "Посчитайте, сколько больше вы могли бы заработать с премиальным планом",
    "vs_savings_if_earn": "Если вы зарабатываете {amount}/месяц",
    "vs_savings_standard_keep": "Стандарт: Сохраняете {amount}",
    "vs_savings_pro_keep": "Pro Premier: Сохраняете {amount}",
    "vs_savings_elite_keep": "Elite VIP: Сохраняете {amount}",
    "vs_faq_title": "Часто задаваемые вопросы",
    "vs_faq_cancel_q": "Могу я отменить в любой момент?",
    "vs_faq_cancel_a": "Да! Вы можете отменить подписку в любое время. Доступ будет активен до конца оплаченного периода.",
    "vs_faq_commission_q": "Когда меняются ставки комиссии?",
    "vs_faq_commission_a": "Ваша новая ставка комиссии вступает в силу сразу после подписки. Все будущие заказы будут с новой ставкой.",
    "vs_faq_payment_q": "Какие способы оплаты принимаются?",
    "vs_faq_payment_a": "Мы принимаем все основные кредитные карты через наш безопасный процессор платежей, Stripe.",
    "vs_toast_cancelled": "Подписка отменена. Теперь вы на Стандартном плане.",
    "vs_toast_failed": "Не удалось обработать подписку",
    "vs_toast_verify_timeout": "Время проверки подписки истекло. Пожалуйста, обновите страницу.",
    "vs_toast_welcome_to": "Добро пожаловать в {name}!",
    "vs_feat_basic_listing": "Базовое размещение",
    "vs_feat_standard_commission": "Стандартная комиссия (20%)",
    "vs_feat_access_offers": "Доступ ко всем предложениям работы",
    "vs_feat_basic_support": "Базовая поддержка",
    "vs_feat_priority_offers": "Приоритетные предложения по работе",
    "vs_feat_featured_placement": "Выделенное размещение",
    "vs_feat_priority_support": "Приоритетная поддержка",
    "vs_feat_lower_commission": "Низкие ставки комиссии",
    "vs_feat_priority_dispatch": "Приоритет в очереди на отправку",
    "vs_feat_reduced_commission": "Уменьшенная комиссия (15%)",
    "vs_feat_featured_search": "Выделение в результатах поиска",
    "vs_feat_priority_support_response": "Приоритетный ответ поддержки",
    "vs_feat_detailed_analytics": "Подробная аналитическая панель",
    "vs_feat_professional_badge": "Значок профессионала в профиле",
    "vs_feat_top_placement": "Топовое размещение во всех поисках",
    "vs_feat_lowest_commission": "Самая низкая комиссия (10%)",
    "vs_feat_first_access_premium": "Первый доступ к премиум-заказам",
    "vs_feat_vip_support": "VIP выделенная линия поддержки",
    "vs_feat_advanced_analytics": "Продвинутая аналитика и инсайты",
    "vs_feat_elite_badge": "Значок элиты в профиле",
    "vs_feat_marketing_features": "Возможности маркетинговых функций",
    "vs_feat_exclusive_events": "Доступ к эксклюзивным мероприятиям",
    "vs_feat_top_placement_guarantee": "Гарантия топового размещения",
    "vs_feat_vip_dedicated_support": "VIP выделенная поддержка"
  },
  // Phase i18n expansion 2026-06-12 — Haitian Creole (new locale).
  ht: {
    "vh_app_pending_title": "Aplikasyon an ap tann revizyon",
    "vh_app_pending_desc": "Nou ap revize aplikasyon vandè ou. N ap notifye ou lè li apwouve (an jeneral 24–48 èdtan).",
    "vh_stripe_pending_title": "Verifikasyon Stripe an pwogrè",
    "vh_stripe_pending_desc": "Ou te soumèt detay ou yo. Stripe ap verifye kont ou — anjeneral kèk minit, pafwa jiska 24 èdtan. Ou ka aksepte ak konplete rezèrvasyon kounye a; genyen pral anpile jiskaske Stripe fini verifye.",
    "vh_stripe_pending_refresh": "Rafrechi",
    "vh_stripe_warning_title": "Konplete Konfigirasyon Peman Stripe",
    "vh_stripe_warning_desc": "Peman pa ap libere jiskaske onboarding Stripe konplete.",
    "vh_stripe_warning_cta": "Konplete",
    "vh_welcome_back": "Byenveni tounen,",
    "vh_signed_in_as": "Konekte kòm {name}",
    "vh_next_best_action_label": "Pwochen pi bon aksyon",
    "vh_action_profile_title": "Konplete pwofil ou",
    "vh_action_profile_desc": "Ajoute non biznis ou ak bio pou kliyan ka jwenn ou epi fè konfyans ou.",
    "vh_action_profile_cta": "Konplete pwofil",
    "vh_action_photos_title": "Ajoute premye foto ou yo",
    "vh_action_photos_desc": "Pwofil ak foto jwenn jiska 5× plis rezèrvasyon. Telechaje omwen youn pou kòmanse.",
    "vh_action_photos_cta": "Voye foto",
    "vh_action_equipment_title": "Amelyore preparasyon ekipman ou",
    "vh_action_equipment_desc": "Ou nan {pct}%. Rive jiska 80%+ pou kalifye pou ekspedisyon priyorite.",
    "vh_action_equipment_cta": "Louvri lis verifikasyon",
    "vh_action_bookings_title": "{count} òf rezèrvasyon ap tann",
    "vh_action_bookings_desc": "Reponn rapidman — òf yo reassign otomatikman apre kèk minit.",
    "vh_action_bookings_cta": "Revize òf yo",
    "vh_action_upgrade_title": "Monte grad pou kenbe plis nan salè ou",
    "vh_action_upgrade_desc": "Vandè Pro Premier kenbe 85% nan chak lave ak jwenn plasman priyorite sou kat la.",
    "vh_action_upgrade_cta": "Gade plan yo",
    "vh_action_allset_title": "Ou pare",
    "vh_action_allset_desc": "Pwofil, foto, ak ekipman sanble anfòm. Kontinye bay sèvis 5-etoile.",
    "vh_action_allset_cta": "Gade travay yo",
    "vh_group_setup_title": "Konfigirasyon ak zouti",
    "vh_group_setup_subtitle": "Prepare pou livre",
    "vh_group_grow_title": "Fè biznis ou grandi",
    "vh_group_grow_subtitle": "Plis travay, pi bon itinerè, plis salè",
    "vh_link_equipment_title": "Lis verifikasyon ekipman",
    "vh_link_equipment_desc": "Swiv preparasyon ekipman ou",
    "vh_link_training_title": "Fòmasyon & Tutorial",
    "vh_link_training_desc": "Aprann kijan pou delivre sèvis 5-etoile",
    "vh_link_listing_title": "Preview lis ou",
    "vh_link_listing_desc": "Gade kijan kliyan wè pwofil ou",
    "vh_link_referrals_title": "Pwogram referans",
    "vh_link_referrals_desc": "Genyen $50 pou chak vandè ou refere",
    "vh_link_schedule_title": "Orè jodi a",
    "vh_link_schedule_desc": "View delè rezèrvasyon jodi a",
    "vh_link_route_title": "Itinéraire entelijan",
    "vh_link_route_desc": "Optimize lòd kondwi ou, sove tan & gaz",
    "vp_header_subtitle_elite": "Vizibilite maksimòm & pi ba frais",
    "vp_header_subtitle_pro": "Vizibilite avanse & karakteristik",
    "vp_header_subtitle_basic": "Lis debaz — monte grad pou plis karakteristik",
    "vp_header_subtitle_luxury": "Plasman tèt nan mache a · komisyon pi ba",
    "vp_upgrade_to_pro_premier": "Monte grad nan Pro Premier",
    "vp_upgrade_to_elite_vip": "Mete ajou pou Elite VIP",
    "vp_upgrade_to_luxury": "Mete ajou pou Luxury Concierge",
    "vp_platform_fee_label": "{pct}% Frè Platfòm",
    "vp_basic_fee_warning_title": "W ap peye 20% pou chak lave",
    "vp_basic_fee_warning_desc": "Mete ajou pou Pro Premier (15%) oswa Elite VIP (10%) pou kenbe plis nan sa ou touche yo.",
    "vp_recent_jobs_title": "Dènye travay yo",
    "vp_recent_jobs_more_hidden": "+{count} lòt travay kache",
    "vp_recent_jobs_upgrade_link": "Mete ajou pou wè tout →",
    "vp_listing_preview_section_title": "Apèsi kliyan an",
    "vp_visibility_max": "Vizibilite maksimòm",
    "vp_visibility_enhanced": "Vizibilite amelyore",
    "vp_visibility_limited": "Vizibilite limite",
    "vp_bio_label": "Biyografi biznis",
    "vp_bio_chars_count": "{count}/{max} karaktè",
    "vp_bio_upgrade_hint": "Mete ajou pou {max} karaktè",
    "vp_bio_basic_lock": "Mete ajou pou Pro Premier pou yon biyografi pi long ak pi detay ki atire plis kliyan.",
    "vp_work_photos_label": "Foto travay yo",
    "vp_photos_count_label": "{count}/{max} foto",
    "vp_add_photo": "Ajoute foto",
    "vp_uploading": "Ap telechaje…",
    "vp_remove_photo": "Retire foto",
    "vp_photos_locked_aria": "Mete ajou pou debloke plis plas foto",
    "vp_photos_copy_basic": "Debaz / Gratis — jiska 2 foto · mete ajou pou Pro Premier pou 5, Elite VIP pou 10, oswa Luxury Concierge pou 15.",
    "vp_photos_copy_pro": "Pro Premier — jiska 5 foto · mete ajou pou Elite VIP pou 10 (oswa Luxury Concierge pou 15).",
    "vp_photos_copy_elite": "Elite VIP — galri 10 foto · vizibilite elaji & plasman lis priyorite · mete ajou pou Luxury pou 15.",
    "vp_photos_copy_luxury": "Luxury Concierge — lis prim tèt · galri 15 foto · pi wo plasman mache.",
    "vp_photos_unlock_more": "Debloke plis foto →",
    "vp_video_portfolio": "Videyo portfolyo",
    "vp_video_elite_exclusive": "Eksklizif Elite",
    "vp_video_add": "Ajoute yon videyo ki montre travay ou",
    "vp_video_up_to_2min": "Jiska 2 minit",
    "vp_video_locked_title": "Videyo portfolyo",
    "vp_video_locked_desc": "Mete ajou pou Elite VIP pou ajoute kontni videyo.",
    "vp_video_locked_cta": "Debloke",
    "va_available": "Disponib",
    "va_not_available": "Pa disponib",
    "va_visible_desc": "Ou vizib pou kliyan yo epi ou ka resevwa rezèrvasyon",
    "va_hidden_desc": "Ou kache nan rechèch yo — pa gen nouvo rezèrvasyon",
    "va_go_offline": "Ale offline",
    "va_go_online": "Ale sou entènèt",
    "va_check_visible_searches": "Vizib nan rechèch yo",
    "va_check_receiving_bookings": "Ap resevwa rezèrvasyon",
    "va_check_on_the_map": "Sou kat la",
    "va_check_customer_notifications": "Notifikasyon kliyan",
    "va_last_updated": "Dènye mete ajou: {time}",
    "va_toggle_error": "Echwe pou mete ajou estati disponiblite a. Tanpri eseye ankò.",
    "vs_page_title": "Plan abònman founisè yo",
    "vs_page_subtitle": "Mete ajou plan ou pou touche plis pou chak travay, jwenn aksè priyorite nan rezèrvasyon yo, e fè kliyan yo remake w.",
    "vs_current_plan": "Plan aktyèl",
    "vs_commission_rate": "To Komisyon",
    "vs_benefit_earn_title": "Griyen plis chak travay",
    "vs_benefit_earn_desc": "Pi ba pousantaj komisyo vle di ou kenbe plis nan sa ou touche. Manm Elite VIP ekonomize jiska 10% pa travay konpare ak Standard.",
    "vs_benefit_priority_title": "Aksè priyoritè a travay",
    "vs_benefit_priority_desc": "Gen opòtinite an premye sou pi bon travay yo. Manm Premium resevwa of pou travay anvan lòt founisè.",
    "vs_benefit_standout_title": "Fè kliyan remake ou",
    "vs_benefit_standout_desc": "Badge Premium ak plasman espesyal ede w atire plis kliyan epi konstwi konfyans.",
    "vs_most_popular_badge": "Pi popilè",
    "vs_per_month": "/mwa",
    "vs_platform_fee_per_wash": "Frè platfòm · {pct}% pa lavage",
    "vs_select_plan": "Chwazi plan",
    "vs_current_plan_badge": "Plan aktyèl",
    "vs_platform_commission": "Komisyon platfòm",
    "vs_monthly_label": "Chak mwa",
    "vs_current_plan_btn": "Plan Aktyèl",
    "vs_upgrade_btn": "Mizajou",
    "vs_switch_plan_btn": "Chanje Plan",
    "vs_savings_card_title": "Wè Ekonomi Ou",
    "vs_savings_card_desc": "Kalkile kòman ou ta ka touche plis ak yon plan prim",
    "vs_savings_if_earn": "Si ou touche {amount}/mwa",
    "vs_savings_standard_keep": "Standard: Kenbe {amount}",
    "vs_savings_pro_keep": "Pro Premier: Kenbe {amount}",
    "vs_savings_elite_keep": "Elite VIP: Kenbe {amount}",
    "vs_faq_title": "Kesyon Souvan Poze",
    "vs_faq_cancel_q": "Èske mwen ka anile nenpòt ki lè?",
    "vs_faq_cancel_a": "Wi! Ou ka anile abònman ou nenpòt ki lè. Ou pral toujou gen aksè jiskaske fen peryòd fakti ou.",
    "vs_faq_commission_q": "Kilè pousantaj komisyo chanje?",
    "vs_faq_commission_a": "Nouvo pousantaj komisyon ou a pran efè imedyat apre abònman. Tout travay k ap vini yo ap itilize nouvo pousantaj ou yo.",
    "vs_faq_payment_q": "Ki metòd peman yo aksepte?",
    "vs_faq_payment_a": "Nou aksepte tout gwo kat kredi atravè pwose kilè peman nou an, Stripe.",
    "vs_toast_cancelled": "Abònman an anile. Ou kounye a sou plan Standard.",
    "vs_toast_failed": "Echwe pou trete abònman",
    "vs_toast_verify_timeout": "Verifikasyon abònman an ekspire. Tanpri chaje paj la ankò.",
    "vs_toast_welcome_to": "Byenveni nan {name}!",
    "vs_feat_basic_listing": "Annons debaz",
    "vs_feat_standard_commission": "Komisyon estanda (20%)",
    "vs_feat_access_offers": "Aksè a tout of travay",
    "vs_feat_basic_support": "Sipò debaz",
    "vs_feat_priority_offers": "Of travay avèk priyorite",
    "vs_feat_featured_placement": "Plasman espesyal",
    "vs_feat_priority_support": "Sipò avèk priyorite",
    "vs_feat_lower_commission": "Pousantaj komisyo pi ba",
    "vs_feat_priority_dispatch": "Priyorite nan liy distribisyon",
    "vs_feat_reduced_commission": "Pousantaj komisyo redwi (15%)",
    "vs_feat_featured_search": "Espesyal nan rezilta rechèch",
    "vs_feat_priority_support_response": "Repons sipò avèk priyorite",
    "vs_feat_detailed_analytics": "Dachbord analiz detaye",
    "vs_feat_professional_badge": "Badge pwofesyonèl sou pwofil",
    "vs_feat_top_placement": "Pi bon plasman nan tout rechèch",
    "vs_feat_lowest_commission": "Pousantaj komisyo pi ba (10%)",
    "vs_feat_first_access_premium": "Premye aksè a travay prim",
    "vs_feat_vip_support": "Liy sipò dedye VIP",
    "vs_feat_advanced_analytics": "Analitik avanse & gwo pèspektiv",
    "vs_feat_elite_badge": "Medaillon Elite sou pwofil",
    "vs_feat_marketing_features": "Opòtinite karakteristik maketing",
    "vs_feat_exclusive_events": "Aksè a evènman eksklizif founisè",
    "vs_feat_top_placement_guarantee": "Garanti plasman nan tèt",
    "vs_feat_vip_dedicated_support": "Sipò dedye VIP"
  },
};

export default VENDOR_PORTAL_I18N;
